import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Bell,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Headphones,
  Inbox,
  ImagePlus,
  FileText,
  LayoutDashboard,
  Loader2,
  MessageCircleMore,
  MessageSquareText,
  MoreHorizontal,
  Phone,
  PhoneCall,
  PhoneMissed,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ScanSearch,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Users,
  Wifi,
  X,
  Trash2,
} from 'lucide-react';
import { AuthenticationRequiredError, communicationsRepository } from './api/communications';
import type { BusinessLine, CallEvent, CommunicationEvent, ContactDirectoryItem, Conversation, DashboardSnapshot, LineWatchNotification, MessageAttachmentUpload, MessageEvent, MessageTemplate, NotificationCategory, PushCategoryPreferences, PushDeliveryDiagnostics, PushPreferences, PushSubscriptionPayload } from './domain';
import { useNavigationHistory, type NavigationSnapshot } from './useNavigationHistory';
import { LookupPage } from './LookupPage';
import { MessageLookup } from './MessageLookup';
import { ContactsPage } from './ContactsPage';

type ActivityFilter = 'all' | 'messages' | 'new' | 'calls' | 'review';
type AppState = 'loading' | 'ready' | 'auth' | 'error';
type ComposerSendPayload = { recipients: string[]; attachment?: File; stagedAttachment?: MessageAttachmentUpload };
type ComposerSendResult = { failedRecipients: string[] } | void;
type MessageThreadAnchor = { businessId: string; phone: string };
type MessageReplyContext = {
  businessId: string;
  phoneNumber: string;
  contactName: string;
  body: string;
  displayTime: string;
  direction: MessageEvent['direction'];
  mediaCount: number;
};
type ContactNameEditorTarget = {
  contactId: string;
  displayName: string;
  phoneNumber: string;
  phoneE164?: string;
  businessId: string;
  eventId: string;
  eventKind: CommunicationEvent['kind'];
};

const MMS_SAFE_MAX_BYTES = 600 * 1024;
const MMS_ACCEPT = 'image/*,video/mp4,video/3gpp,video/quicktime,audio/mpeg,audio/wav,audio/amr,audio/ogg,application/pdf,text/vcard,text/calendar,.vcf,.ics';
const MMS_DIRECT_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'video/mp4', 'video/3gpp', 'video/quicktime',
  'audio/mpeg', 'audio/wav', 'audio/amr', 'audio/ogg',
  'application/pdf', 'text/vcard', 'text/calendar',
]);

const filterLabels: Record<ActivityFilter, string> = {
  all: 'All activity',
  new: 'New messages',
  messages: 'Messages',
  calls: 'Calls',
  review: 'Needs review',
};
const activityTabs: ActivityFilter[] = ['all', 'messages', 'calls', 'review'];

function readInitialAppRoute(): { interactionId: string | null; interactionKind: 'message' | 'call' | null; businessId: string | null; lookupId: string | null } {
  const params = new URLSearchParams(window.location.search);
  const interactionId = params.get('interaction');
  const kind = params.get('kind');
  const interactionKind = kind === 'message' || kind === 'call' ? kind : null;
  const validInteraction = Boolean(interactionId && interactionKind);
  return {
    interactionId: validInteraction ? interactionId : null,
    interactionKind: validInteraction ? interactionKind : null,
    businessId: validInteraction ? params.get('business') : null,
    lookupId: params.get('lookup'),
  };
}

function App() {
  const initialRoute = useMemo(readInitialAppRoute, []);
  const [snapshot, setSnapshot] = useState<DashboardSnapshot | null>(null);
  const [appState, setAppState] = useState<AppState>('loading');
  const [loadError, setLoadError] = useState('');
  const [businessId, setBusinessId] = useState(() => initialRoute.businessId || localStorage.getItem('linewatch-business') || 'all');
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(initialRoute.interactionId);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(Boolean(initialRoute.interactionId));
  const [businessPickerOpen, setBusinessPickerOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeBusinessId, setComposeBusinessId] = useState('');
  const [composeTo, setComposeTo] = useState('');
  const [composeText, setComposeText] = useState('');
  const [composeReplyContext, setComposeReplyContext] = useState<MessageReplyContext | null>(null);
  const [sendState, setSendState] = useState<'idle' | 'sending'>('idle');
  const [sendError, setSendError] = useState('');
  const [callOpen, setCallOpen] = useState(false);
  const [callBusinessId, setCallBusinessId] = useState('');
  const [callTo, setCallTo] = useState('');
  const [callState, setCallState] = useState<'idle' | 'starting' | 'active'>('idle');
  const [callError, setCallError] = useState('');
  const [activeCall, setActiveCall] = useState<CallEvent | null>(null);
  const [callDetail, setCallDetail] = useState<CallEvent | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const initialLookupId = initialRoute.lookupId;
  const [lookupOpen, setLookupOpen] = useState(Boolean(initialLookupId));
  const [lookupId, setLookupId] = useState<string | null>(initialLookupId);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [contactId, setContactId] = useState<string | null>(null);
  const [contactBusinessId, setContactBusinessId] = useState<string | null>(null);
  const [contactPhoneE164, setContactPhoneE164] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<LineWatchNotification[]>([]);
  const [unseenNotifications, setUnseenNotifications] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [reviewDismissingId, setReviewDismissingId] = useState<string | null>(null);
  const [notificationClearingId, setNotificationClearingId] = useState<string | null>(null);
  const [messageDeletingId, setMessageDeletingId] = useState<string | null>(null);
  const [conversationDeleting, setConversationDeleting] = useState(false);
  const [callDeletingId, setCallDeletingId] = useState<string | null>(null);
  const [contactNameEditor, setContactNameEditor] = useState<ContactNameEditorTarget | null>(null);
  const [toast, setToast] = useState('');

  const applyNavigationSnapshot = useCallback((next: NavigationSnapshot) => {
    setBusinessId(next.businessId);
    const restoredFilter = String(next.filter) === 'unread' ? 'new' : next.filter;
    setFilter(restoredFilter);
    setQuery(next.query);
    setSelectedId(next.selectedId);
    setMobileDetailOpen(next.mobileDetailOpen);
    setBusinessPickerOpen(next.businessPickerOpen);
    setComposeOpen(next.composeOpen);
    setComposeBusinessId(next.composeBusinessId || '');
    setComposeTo(next.composeTo || '');
    setComposeText(next.composeText || '');
    setCallOpen(next.callOpen);
    setCallBusinessId(next.callBusinessId || '');
    setCallTo(next.callTo || '');
    setSettingsOpen(next.settingsOpen);
    setNotificationsOpen(Boolean(next.notificationsOpen));
    setLookupOpen(Boolean(next.lookupOpen));
    setLookupId(next.lookupId || null);
    setContactsOpen(Boolean(next.contactsOpen));
    setContactId(next.contactId || null);
    setContactBusinessId(next.contactBusinessId || null);
    setContactPhoneE164(next.contactPhoneE164 || null);
  }, []);

  const navigationSnapshot: NavigationSnapshot = {
    businessId,
    filter,
    query,
    selectedId,
    mobileDetailOpen,
    businessPickerOpen,
    composeOpen,
    composeBusinessId,
    composeTo,
    composeText,
    callOpen,
    callBusinessId,
    callTo,
    settingsOpen,
    notificationsOpen,
    lookupOpen,
    lookupId,
    contactsOpen,
    contactId,
    contactBusinessId,
    contactPhoneE164,
  };
  const navigation = useNavigationHistory(navigationSnapshot, applyNavigationSnapshot, appState === 'ready' && Boolean(snapshot));

  const refreshDashboard = useCallback(async (silent = false, preferredThread?: MessageThreadAnchor) => {
    if (!silent) setAppState('loading');
    try {
      const [dashboardData, notificationFeed] = await Promise.all([
        communicationsRepository.getDashboardSnapshot(),
        communicationsRepository.getNotifications(),
      ]);
      let data = dashboardData;
      if (initialRoute.interactionId && initialRoute.interactionKind && !data.events.some(event => event.id === initialRoute.interactionId)) {
        try {
          const directEvent = initialRoute.interactionKind === 'message'
            ? await communicationsRepository.getMessage(initialRoute.interactionId)
            : await communicationsRepository.getCall(initialRoute.interactionId);
          data = { ...data, events: [directEvent, ...data.events] };
        } catch {
          // The regular dashboard still loads if a stale or deleted deep link cannot be resolved.
        }
      }
      setSnapshot(data);
      setNotifications(notificationFeed.notifications);
      setUnseenNotifications(notificationFeed.unseenCount);
      setNotificationsLoading(false);
      setSelectedId(current => {
        if (current && data.events.some(event => event.id === current)) return current;
        if (preferredThread) {
          const replacement = data.events.find(event => (
            event.kind === 'message' &&
            event.businessId === preferredThread.businessId &&
            (event.contact.phoneE164 || event.contact.phoneNumber) === preferredThread.phone
          ));
          if (replacement) return replacement.id;
        }
        return data.events[0]?.id ?? null;
      });
      setAppState('ready');
      setLoadError('');
    } catch (error) {
      if (error instanceof AuthenticationRequiredError) {
        setAppState('auth');
      } else if (!silent) {
        setLoadError(error instanceof Error ? error.message : 'The communications service is unavailable.');
        setAppState('error');
      }
    }
  }, [initialRoute.interactionId, initialRoute.interactionKind]);

  useEffect(() => {
    if (!notificationsOpen) return;
    // A delivered Web Push can outlive its server-side unread count (notably test
    // notifications). Always clear the device tray when the notification center
    // is opened, even when the LineWatch badge is already zero.
    void closeDeliveredNotifications();
    if (!unseenNotifications) return;
    const seenAt = new Date().toISOString();
    setNotifications(current => current.map(item => item.seenAt ? item : { ...item, seenAt }));
    setUnseenNotifications(0);
    void communicationsRepository.markAllNotificationsSeen().catch(() => void refreshDashboard(true));
  }, [notificationsOpen, unseenNotifications, refreshDashboard]);

  useEffect(() => {
    void syncAppBadge(unseenNotifications);
  }, [unseenNotifications]);

  useEffect(() => {
    if (appState === 'ready') void closeDeliveredNotifications();
  }, [appState]);

  useEffect(() => {
    void refreshDashboard();
  }, [refreshDashboard]);

  useEffect(() => {
    if (appState !== 'ready') return;
    const timer = window.setInterval(() => void refreshDashboard(true), 15_000);
    return () => window.clearInterval(timer);
  }, [appState, refreshDashboard]);

  useEffect(() => {
    localStorage.setItem('linewatch-business', businessId);
  }, [businessId]);

  useEffect(() => {
    navigation.sync({ query });
  }, [query, navigation.sync]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const businesses = snapshot?.businesses ?? [];

  useEffect(() => {
    if (businessId !== 'all' && businesses.length && !businesses.some(line => line.id === businessId)) setBusinessId('all');
  }, [businessId, businesses]);

  const visibleEvents = useMemo(() => {
    if (!snapshot) return [];
    const normalized = query.trim().toLowerCase();
    const filtered = snapshot.events.filter(event => {
      const matchesBusiness = businessId === 'all' || event.businessId === businessId;
      const matchesFilter =
        filter === 'all' ||
        (filter === 'messages' && event.kind === 'message') ||
        (filter === 'new' && event.kind === 'message' && event.direction === 'inbound' && event.unread) ||
        (filter === 'calls' && event.kind === 'call') ||
        (filter === 'review' && event.needsReview);
      const business = businesses.find(item => item.id === event.businessId);
      const searchable = [
        event.contact.name,
        event.contact.phoneNumber,
        event.contact.company,
        business?.name,
        event.kind === 'message' ? event.body : event.transcriptPreview,
      ].filter(Boolean).join(' ').toLowerCase();
      return matchesBusiness && matchesFilter && (!normalized || searchable.includes(normalized));
    });
    const seenThreads = new Set<string>();
    return filtered.filter(event => {
      if (event.kind !== 'message') return true;
      const phone = event.contact.phoneE164 || event.contact.phoneNumber;
      const key = `${event.businessId}:${phone}`;
      if (seenThreads.has(key)) return false;
      seenThreads.add(key);
      return true;
    });
  }, [snapshot, businessId, filter, query, businesses]);

  const messageThreadCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const event of snapshot?.events || []) {
      if (event.kind !== 'message') continue;
      const phone = event.contact.phoneE164 || event.contact.phoneNumber;
      const key = `${event.businessId}:${phone}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }, [snapshot]);

  useEffect(() => {
    if (mobileDetailOpen) {
      if (snapshot?.events.some(event => event.id === selectedId)) return;
      setMobileDetailOpen(false);
    }
    if (!visibleEvents.some(event => event.id === selectedId)) setSelectedId(visibleEvents[0]?.id ?? null);
  }, [visibleEvents, selectedId, mobileDetailOpen, snapshot]);

  const selectedEvent = visibleEvents.find(event => event.id === selectedId)
    ?? snapshot?.events.find(event => event.id === selectedId)
    ?? null;
  const selectedBusiness = businesses.find(item => item.id === businessId) ?? null;
  const communicationPhone = selectedEvent?.contact.phoneE164 || selectedEvent?.contact.phoneNumber || '';
  const contextualPhone = contactsOpen ? contactPhoneE164 || '' : communicationPhone;

  useEffect(() => {
    if (!composeOpen || composeTo || !contextualPhone) return;
    setComposeTo(contextualPhone);
    navigation.sync({ composeTo: contextualPhone });
  }, [composeOpen, contextualPhone, navigation.sync]);

  useEffect(() => {
    if (!callOpen || callTo || !contextualPhone) return;
    setCallTo(contextualPhone);
    navigation.sync({ callTo: contextualPhone });
  }, [callOpen, contextualPhone, navigation.sync]);

  useEffect(() => {
    if (!selectedEvent || selectedEvent.kind !== 'message') {
      setConversation(null);
      return;
    }
    const phone = selectedEvent.contact.phoneE164 || selectedEvent.contact.phoneNumber;
    let cancelled = false;
    setConversationLoading(true);
    communicationsRepository.getConversation(selectedEvent.businessId, phone)
      .then(data => {
        if (!cancelled) setConversation(data);
      })
      .catch(() => { if (!cancelled) setConversation(null); })
      .finally(() => { if (!cancelled) setConversationLoading(false); });
    return () => { cancelled = true; };
  }, [selectedEvent?.id]);

  const markMessageRead = useCallback((event: MessageEvent) => {
    const phone = event.contact.phoneE164 || event.contact.phoneNumber;
    setSnapshot(current => {
      if (!current) return current;
      const unreadInConversation = current.events.filter(item =>
        item.kind === 'message' &&
        item.unread &&
        item.businessId === event.businessId &&
        (item.contact.phoneE164 || item.contact.phoneNumber) === phone,
      ).length;
      if (!unreadInConversation) return current;
      return {
        ...current,
        events: current.events.map(item =>
          item.kind === 'message' &&
          item.businessId === event.businessId &&
          (item.contact.phoneE164 || item.contact.phoneNumber) === phone
            ? { ...item, unread: false }
            : item,
        ),
        businesses: current.businesses.map(line => line.id === event.businessId
          ? {
              ...line,
              unreadCount: Math.max(0, line.unreadCount - unreadInConversation),
              newMessageCount: Math.max(0, line.newMessageCount - unreadInConversation),
            }
          : line),
        totals: {
          ...current.totals,
          unread: Math.max(0, current.totals.unread - unreadInConversation),
          newMessages: Math.max(0, current.totals.newMessages - unreadInConversation),
        },
      };
    });
    void communicationsRepository.markConversationRead(event.businessId, phone)
      .then(() => refreshDashboard(true))
      .catch(() => refreshDashboard(true));
  }, [refreshDashboard]);

  const markCallReviewed = useCallback((event: CallEvent) => {
    setSnapshot(current => {
      if (!current) return current;
      const reviewDelta = event.needsReview ? 1 : 0;
      return {
        ...current,
        events: current.events.map(item => item.id === event.id ? { ...item, unread: false, needsReview: false, reviewReason: undefined } : item),
        businesses: current.businesses.map(line => line.id === event.businessId
          ? { ...line, needsReviewCount: Math.max(0, line.needsReviewCount - reviewDelta) }
          : line),
        totals: { ...current.totals, needsReview: Math.max(0, current.totals.needsReview - reviewDelta) },
      };
    });
    void communicationsRepository.markCallReviewed(event.id)
      .then(() => refreshDashboard(true))
      .catch(() => refreshDashboard(true));
  }, [refreshDashboard]);

  const dismissNeedsReview = useCallback(async (kind: 'message' | 'call', communicationId: string) => {
    if (reviewDismissingId === communicationId) return;
    setReviewDismissingId(communicationId);
    setCallDetail(current => current?.id === communicationId ? { ...current, needsReview: false, reviewReason: undefined } : current);
    setSnapshot(current => {
      if (!current) return current;
      const target = current.events.find(item => item.id === communicationId && item.kind === kind);
      if (!target?.needsReview) return current;
      return {
        ...current,
        events: current.events.map(item => item.id === communicationId ? { ...item, needsReview: false, reviewReason: undefined } : item),
        businesses: current.businesses.map(line => line.id === target.businessId
          ? { ...line, needsReviewCount: Math.max(0, line.needsReviewCount - 1) }
          : line),
        totals: { ...current.totals, needsReview: Math.max(0, current.totals.needsReview - 1) },
      };
    });
    setNotifications(current => current.filter(item => !(item.category === 'needsReview' && item.kind === kind && item.communicationId === communicationId)));
    try {
      await communicationsRepository.dismissNeedsReview(kind, communicationId);
      setToast('Needs attention dismissed.');
    } catch {
      setToast('Could not dismiss this item. Please try again.');
    } finally {
      await refreshDashboard(true);
      setReviewDismissingId(null);
    }
  }, [refreshDashboard, reviewDismissingId]);

  const clearNotification = useCallback(async (notification: LineWatchNotification) => {
    if (notificationClearingId === notification.id) return;
    setNotificationClearingId(notification.id);
    setNotifications(current => current.filter(item => item.id !== notification.id));
    try {
      await communicationsRepository.dismissNotification(notification.id);
      await closeDeliveredNotifications();
      await refreshDashboard(true);
      setToast(`${notification.businessName} notification cleared.`);
    } catch {
      setToast('Could not clear this notification. Please try again.');
      await refreshDashboard(true);
    } finally {
      setNotificationClearingId(null);
    }
  }, [notificationClearingId, refreshDashboard]);

  const deleteMessageFromLineWatch = useCallback(async (message: MessageEvent) => {
    if (messageDeletingId || conversationDeleting) return;
    if (!window.confirm('Delete this message from LineWatch? The original message will remain in Telnyx.')) return;
    setMessageDeletingId(message.id);
    try {
      await communicationsRepository.deleteMessage(message.id);
      setConversation(current => current ? { ...current, messages: current.messages.filter(item => item.id !== message.id) } : current);
      await refreshDashboard(true, {
        businessId: message.businessId,
        phone: message.contact.phoneE164 || message.contact.phoneNumber,
      });
      setToast('Message deleted from LineWatch. Telnyx was not changed.');
    } catch {
      setToast('Could not delete this message. Please try again.');
      await refreshDashboard(true);
    } finally {
      setMessageDeletingId(null);
    }
  }, [conversationDeleting, messageDeletingId, refreshDashboard]);

  const deleteConversationFromLineWatch = useCallback(async (event: MessageEvent) => {
    if (conversationDeleting || messageDeletingId) return;
    const phone = event.contact.phoneE164 || event.contact.phoneNumber;
    if (!window.confirm(`Delete this conversation with ${event.contact.name} from LineWatch? All visible messages in this thread will be removed, but Telnyx will remain unchanged.`)) return;
    setConversationDeleting(true);
    try {
      const hiddenCount = await communicationsRepository.deleteConversation(event.businessId, phone);
      setConversation(null);
      navigation.replace({ selectedId: null, mobileDetailOpen: false });
      await refreshDashboard(true);
      setToast(`${hiddenCount} message${hiddenCount === 1 ? '' : 's'} deleted from LineWatch. Telnyx was not changed.`);
    } catch {
      setToast('Could not delete this conversation. Please try again.');
      await refreshDashboard(true);
    } finally {
      setConversationDeleting(false);
    }
  }, [conversationDeleting, messageDeletingId, navigation.replace, refreshDashboard]);

  const deleteCallFromLineWatch = useCallback(async (event: CallEvent) => {
    if (callDeletingId) return;
    if (!window.confirm(`Delete this call with ${event.contact.name} from LineWatch? The Telnyx call and recording data will remain unchanged.`)) return;
    setCallDeletingId(event.id);
    try {
      await communicationsRepository.deleteCall(event.id);
      setCallDetail(null);
      navigation.replace({ selectedId: null, mobileDetailOpen: false });
      await refreshDashboard(true);
      setToast('Call deleted from LineWatch. Telnyx was not changed.');
    } catch {
      setToast('Could not delete this call. Please try again.');
      await refreshDashboard(true);
    } finally {
      setCallDeletingId(null);
    }
  }, [callDeletingId, navigation.replace, refreshDashboard]);

  const openContactNameEditor = useCallback((event: CommunicationEvent) => {
    setContactNameEditor({
      contactId: event.contact.id,
      displayName: event.contact.name,
      phoneNumber: event.contact.phoneNumber,
      phoneE164: event.contact.phoneE164,
      businessId: event.businessId,
      eventId: event.id,
      eventKind: event.kind,
    });
  }, []);

  const saveContactName = useCallback(async (displayName: string) => {
    if (!contactNameEditor) return;
    const phone = contactNameEditor.phoneE164 || contactNameEditor.phoneNumber;
    const updated = await communicationsRepository.updateContact(contactNameEditor.contactId, {
      displayName,
      phoneNumber: phone,
    });
    const refreshedCall = contactNameEditor.eventKind === 'call'
      ? await communicationsRepository.getCall(contactNameEditor.eventId).catch(() => null)
      : null;
    if (refreshedCall) setCallDetail(refreshedCall);
    await refreshDashboard(true, contactNameEditor.eventKind === 'message'
      ? { businessId: contactNameEditor.businessId, phone }
      : undefined);
    setContactNameEditor(null);
    setToast(`${updated.displayName} saved to contacts.`);
  }, [contactNameEditor, refreshDashboard]);

  useEffect(() => {
    if (!selectedEvent || selectedEvent.kind !== 'call') {
      setCallDetail(null);
      return;
    }
    let cancelled = false;
    communicationsRepository.getCall(selectedEvent.id)
      .then(call => { if (!cancelled) setCallDetail(call); })
      .catch(() => { if (!cancelled) setCallDetail(null); });
    return () => { cancelled = true; };
  }, [selectedEvent?.id]);

  const chooseEvent = (id: string) => {
    const event = visibleEvents.find(item => item.id === id) || snapshot?.events.find(item => item.id === id);
    if (event?.kind === 'message' && event.unread) markMessageRead(event);
    if (event?.kind === 'call' && event.unread && !event.needsReview) markCallReviewed(event);
    navigation.push({
      selectedId: id,
      mobileDetailOpen: true,
      businessPickerOpen: false,
      composeOpen: false,
      callOpen: false,
      settingsOpen: false,
      notificationsOpen: false,
      lookupOpen: false,
      lookupId: null,
      contactsOpen: false,
      contactId: null,
      contactBusinessId: null,
      contactPhoneE164: null,
    });
  };

  const updateBusinessColor = async (id: string, color: string) => {
    await communicationsRepository.updateBusinessColor(id, color);
    await refreshDashboard(true);
    setToast('Business color updated.');
  };

  const uploadBusinessLogo = async (id: string, file: File) => {
    await communicationsRepository.uploadBusinessLogo(id, file);
    await refreshDashboard(true);
    setToast('Business logo updated.');
  };

  const removeBusinessLogo = async (id: string) => {
    await communicationsRepository.removeBusinessLogo(id);
    await refreshDashboard(true);
    setToast('Business logo removed.');
  };

  const chooseBusiness = (id: string) => {
    const next = {
      businessId: id,
      businessPickerOpen: false,
      mobileDetailOpen: false,
      composeOpen: false,
      callOpen: false,
      settingsOpen: false,
      notificationsOpen: false,
      lookupOpen: false,
      lookupId: null,
      contactId: contactsOpen ? null : contactId,
      contactBusinessId: contactsOpen ? null : contactBusinessId,
      contactPhoneE164: contactsOpen ? null : contactPhoneE164,
    };
    if (businessPickerOpen) navigation.replace(next);
    else if (id !== businessId) navigation.push(next);
  };

  const chooseFilter = (nextFilter: ActivityFilter) => {
    if (nextFilter === filter && !mobileDetailOpen && !businessPickerOpen && !composeOpen && !callOpen && !settingsOpen && !notificationsOpen && !lookupOpen && !contactsOpen) return;
    navigation.push({
      filter: nextFilter,
      mobileDetailOpen: false,
      businessPickerOpen: false,
      composeOpen: false,
      callOpen: false,
      settingsOpen: false,
      notificationsOpen: false,
      lookupOpen: false,
      lookupId: null,
      contactsOpen: false,
      contactId: null,
      contactBusinessId: null,
      contactPhoneE164: null,
    });
  };

  const chooseQueue = (nextFilter: ActivityFilter, nextBusinessId = businessId) => {
    navigation.push({
      filter: nextFilter,
      businessId: nextBusinessId,
      mobileDetailOpen: false,
      businessPickerOpen: false,
      composeOpen: false,
      callOpen: false,
      settingsOpen: false,
      notificationsOpen: false,
      lookupOpen: false,
      lookupId: null,
      contactsOpen: false,
      contactId: null,
      contactBusinessId: null,
      contactPhoneE164: null,
    });
  };

  const openBusinessPicker = () => navigation.push({
    businessPickerOpen: true,
    composeOpen: false,
    callOpen: false,
    settingsOpen: false,
    notificationsOpen: false,
    lookupOpen: false,
    lookupId: null,
  });

  const openSettings = () => navigation.push({
    settingsOpen: true,
    businessPickerOpen: false,
    composeOpen: false,
    callOpen: false,
    notificationsOpen: false,
    lookupOpen: false,
    lookupId: null,
  });

  const openNotifications = () => navigation.push({
    notificationsOpen: true,
    businessPickerOpen: false,
    composeOpen: false,
    callOpen: false,
    settingsOpen: false,
    lookupOpen: false,
    lookupId: null,
  });

  const openLookup = () => navigation.push({
    lookupOpen: true,
    lookupId: null,
    mobileDetailOpen: false,
    businessPickerOpen: false,
    composeOpen: false,
    callOpen: false,
    settingsOpen: false,
    notificationsOpen: false,
    contactsOpen: false,
    contactId: null,
    contactBusinessId: null,
    contactPhoneE164: null,
  });

  const openContacts = () => navigation.push({
    contactsOpen: true,
    contactId: null,
    contactBusinessId: null,
    contactPhoneE164: null,
    lookupOpen: false,
    lookupId: null,
    mobileDetailOpen: false,
    businessPickerOpen: false,
    composeOpen: false,
    callOpen: false,
    settingsOpen: false,
    notificationsOpen: false,
  });

  const chooseContactBusiness = (id: string) => navigation.push({
    businessId: id,
    contactsOpen: true,
    contactId: null,
    contactBusinessId: null,
    contactPhoneE164: null,
    businessPickerOpen: false,
    composeOpen: false,
    callOpen: false,
    settingsOpen: false,
    notificationsOpen: false,
    lookupOpen: false,
    lookupId: null,
    mobileDetailOpen: false,
  });

  const openContact = (nextContactId: string, nextBusinessId: string, nextPhoneE164: string) => navigation.push({
    contactsOpen: true,
    contactId: nextContactId,
    contactBusinessId: nextBusinessId,
    contactPhoneE164: nextPhoneE164,
    businessPickerOpen: false,
    composeOpen: false,
    callOpen: false,
    settingsOpen: false,
    notificationsOpen: false,
    lookupOpen: false,
    lookupId: null,
    mobileDetailOpen: false,
  });

  const openLookupForEvent = async (event: CommunicationEvent) => {
    navigation.push({
      lookupOpen: true,
      lookupId: null,
      mobileDetailOpen: false,
      businessPickerOpen: false,
      composeOpen: false,
      callOpen: false,
      settingsOpen: false,
      notificationsOpen: false,
      contactsOpen: false,
      contactId: null,
      contactBusinessId: null,
      contactPhoneE164: null,
    });
    try {
      const result = await communicationsRepository.createLookup({
        type: 'phone',
        query: event.contact.phoneE164 || event.contact.phoneNumber,
        businessId: event.businessId,
        contactId: event.contact.id,
        communicationId: event.id,
        communicationKind: event.kind,
        strategy: 'standard',
      });
      navigation.replace({ lookupOpen: true, lookupId: result.id });
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'The caller lookup could not be started.');
    }
  };

  const openNotificationEvent = (notification: LineWatchNotification) => {
    if (!snapshot?.events.some(event => event.id === notification.communicationId)) {
      setToast('That interaction is outside the current activity window.');
      navigation.dismiss({ notificationsOpen: false });
      return;
    }
    navigation.push({
      selectedId: notification.communicationId,
      businessId: notification.businessId,
      filter: 'all',
      query: '',
      mobileDetailOpen: true,
      notificationsOpen: false,
      businessPickerOpen: false,
      composeOpen: false,
      callOpen: false,
      settingsOpen: false,
      lookupOpen: false,
      lookupId: null,
      contactsOpen: false,
      contactId: null,
      contactBusinessId: null,
      contactPhoneE164: null,
    });
  };

  const resolveContactPhone = async (contactId: string | undefined, business: string, fallback: string) => {
    if (!contactId) return fallback;
    try {
      const detail = await communicationsRepository.getContact(contactId, business || 'all');
      return detail.phoneE164 || fallback;
    } catch {
      return fallback;
    }
  };

  const openComposer = async (event?: CommunicationEvent | null) => {
    const lineId = event?.businessId || (businessId === 'all' ? businesses[0]?.id : businessId) || businesses[0]?.id || '';
    const fallbackPhone = event?.contact.phoneE164 || event?.contact.phoneNumber || '';
    const recipient = event ? await resolveContactPhone(event.contact.id, lineId, fallbackPhone) : '';
    setSendError('');
    setComposeReplyContext(event?.kind === 'message' ? {
      businessId: event.businessId,
      phoneNumber: event.contact.phoneE164 || event.contact.phoneNumber,
      contactName: event.contact.name,
      body: event.body,
      displayTime: event.displayTime,
      direction: event.direction,
      mediaCount: event.mediaCount || 0,
    } : null);
    navigation.push({
      composeOpen: true,
      composeBusinessId: lineId,
      composeTo: recipient,
      composeText: '',
      businessPickerOpen: false,
      callOpen: false,
      settingsOpen: false,
      notificationsOpen: false,
      lookupOpen: false,
      lookupId: null,
    });
  };

  const openCall = async (event?: CommunicationEvent | null) => {
    const lineId = event?.businessId || (businessId === 'all' ? businesses[0]?.id : businessId) || businesses[0]?.id || '';
    const fallbackPhone = event?.contact.phoneE164 || event?.contact.phoneNumber || '';
    const recipient = event ? await resolveContactPhone(event.contact.id, lineId, fallbackPhone) : '';
    setCallError('');
    setActiveCall(null);
    setCallState('idle');
    navigation.push({
      callOpen: true,
      callBusinessId: lineId,
      callTo: recipient,
      businessPickerOpen: false,
      composeOpen: false,
      settingsOpen: false,
      notificationsOpen: false,
      lookupOpen: false,
      lookupId: null,
    });
  };

  const openComposerForContact = async (contact: { contactId: string; businessId: string; phoneE164: string }) => {
    const recipient = await resolveContactPhone(contact.contactId, contact.businessId, contact.phoneE164);
    setSendError('');
    setComposeReplyContext(null);
    navigation.push({
      composeOpen: true,
      composeBusinessId: contact.businessId,
      composeTo: recipient,
      composeText: '',
      businessPickerOpen: false,
      callOpen: false,
      settingsOpen: false,
      notificationsOpen: false,
      lookupOpen: false,
      lookupId: null,
    });
  };

  const openCallForContact = async (contact: { contactId: string; businessId: string; phoneE164: string }) => {
    const recipient = await resolveContactPhone(contact.contactId, contact.businessId, contact.phoneE164);
    setCallError('');
    setActiveCall(null);
    setCallState('idle');
    navigation.push({
      callOpen: true,
      callBusinessId: contact.businessId,
      callTo: recipient,
      businessPickerOpen: false,
      composeOpen: false,
      settingsOpen: false,
      notificationsOpen: false,
      lookupOpen: false,
      lookupId: null,
    });
  };

  const startCall = async () => {
    const settings = snapshot?.callSettings;
    if (!settings?.enabled) {
      setCallError('Add your verified cellphone and Telnyx Call Control connection in the server settings first.');
      return;
    }
    setCallState('starting');
    setCallError('');
    try {
      const call = await communicationsRepository.startClickToCall({
        businessId: callBusinessId,
        destinationNumber: callTo,
        bridgeNumberId: settings.bridgeNumberId,
        idempotencyKey: crypto.randomUUID(),
      });
      setActiveCall(call);
      setCallState('active');
      await refreshDashboard(true);
    } catch (error) {
      setCallError(error instanceof Error ? error.message : 'The call could not be started.');
      setCallState('idle');
    }
  };

  useEffect(() => {
    if (!activeCall || ['completed', 'failed', 'cancelled', 'missed'].includes(activeCall.status)) return;
    const timer = window.setInterval(() => {
      void communicationsRepository.getCall(activeCall.id).then(call => {
        setActiveCall(call);
        if (['completed', 'failed', 'cancelled', 'missed'].includes(call.status)) void refreshDashboard(true);
      }).catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [activeCall?.id, activeCall?.status, refreshDashboard]);

  useEffect(() => {
    if (!settingsOpen && !callOpen && !composeOpen && !businessPickerOpen && !mobileDetailOpen && !contactId && !contactsOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (settingsOpen) navigation.dismiss({ settingsOpen: false });
      else if (callOpen) navigation.dismiss({ callOpen: false });
      else if (composeOpen) navigation.dismiss({ composeOpen: false });
      else if (businessPickerOpen) navigation.dismiss({ businessPickerOpen: false });
      else if (mobileDetailOpen) navigation.dismiss({ mobileDetailOpen: false });
      else if (contactId) navigation.dismiss({ contactId: null, contactBusinessId: null });
      else navigation.dismiss({ contactsOpen: false });
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [settingsOpen, callOpen, composeOpen, businessPickerOpen, mobileDetailOpen, contactId, contactsOpen, navigation.dismiss]);

  const sendMessage = async ({ recipients, attachment, stagedAttachment }: ComposerSendPayload) => {
    if (sendState === 'sending') return;
    if (!composeBusinessId || !recipients.length || (!composeText.trim() && !attachment && !stagedAttachment)) {
      setSendError('Choose a line and recipient, then write a message or attach a file.');
      return;
    }
    setSendState('sending');
    setSendError('');
    try {
      const preparedAttachment = attachment ? await prepareMmsAttachment(attachment) : undefined;
      const uploadedAttachment = preparedAttachment
        ? await communicationsRepository.uploadMessageAttachment(preparedAttachment)
        : stagedAttachment;
      const mediaObjectKeys = uploadedAttachment ? [uploadedAttachment.objectKey] : undefined;
      if (recipients.length > 1) {
        const batch = await communicationsRepository.sendBatchMessage({
          businessId: composeBusinessId,
          recipients,
          text: composeText,
          mediaObjectKeys,
          idempotencyKey: crypto.randomUUID(),
        });
        await refreshDashboard(true);
        if (batch.failedCount) {
          setSendError(`${batch.sentCount} sent; ${batch.failedCount} failed. Only the failed recipients remain selected for a safe retry.`);
          return { failedRecipients: batch.results.filter(result => result.status === 'failed').map(result => result.to) };
        }
        navigation.dismiss({ composeOpen: false });
        setToast(`${batch.sentCount} individual messages accepted by Telnyx.`);
        return;
      }
      const message = await communicationsRepository.sendMessage({
        businessId: composeBusinessId,
        to: recipients[0]!,
        text: composeText,
        mediaObjectKeys,
        idempotencyKey: crypto.randomUUID(),
      });
      await refreshDashboard(true);
      navigation.replace({ selectedId: message.id, composeOpen: false });
      setToast(snapshot?.providerState === 'connected'
        ? uploadedAttachment ? 'MMS attachment accepted by Telnyx.' : 'Message accepted by Telnyx.'
        : 'Message sent in safe review mode.');
    } catch (error) {
      setSendError(error instanceof Error ? error.message : 'Message could not be sent.');
    } finally {
      setSendState('idle');
    }
  };

  if (appState === 'loading' || (!snapshot && appState === 'ready')) return <LoadingScreen />;
  if (appState === 'auth') return <LoginScreen onSignedIn={() => refreshDashboard()} />;
  if (appState === 'error' || !snapshot) return <ServiceError message={loadError} onRetry={() => refreshDashboard()} />;

  const syncLabel = snapshot.providerState === 'connected' ? 'Telnyx connected' : snapshot.providerState === 'mock' ? 'Safe review mode' : 'Telnyx setup needed';

  return (
    <>
      <div className="app-shell">
      <aside className="business-rail" aria-label="Business lines">
        <BrandMark />
        <div className="rail-section-label">Lines</div>
        <button className={`business-button all-lines ${businessId === 'all' ? 'active' : ''}`} onClick={() => chooseBusiness('all')}>
          <span className="business-avatar all-avatar"><Inbox size={17} /></span>
          <span className="business-copy"><strong>All lines</strong><small>Unified activity</small></span>
          <CountBadge count={snapshot.totals.unread} />
        </button>
        <div className="business-list">
          {businesses.map(business => <BusinessButton key={business.id} business={business} active={business.id === businessId} onClick={() => chooseBusiness(business.id)} />)}
        </div>
        <button className={`lookup-rail-action ${contactsOpen ? 'active' : ''}`} onClick={openContacts}><Users size={17} /><span><strong>Contacts</strong><small>People across every line</small></span><ChevronRight size={14} /></button>
        {snapshot.features?.lookup && <button className={`lookup-rail-action ${lookupOpen ? 'active' : ''}`} onClick={openLookup}><ScanSearch size={17} /><span><strong>Lookup</strong><small>People, phone & property</small></span><ChevronRight size={14} /></button>}
        <div className="rail-footer">
          <div className="secure-state"><ShieldCheck size={15} /><span>Private workspace</span></div>
        </div>
      </aside>

      {contactsOpen ? <ContactsPage
        businesses={businesses}
        businessId={businessId}
        contactId={contactId}
        contactBusinessId={contactBusinessId}
        unseenNotifications={unseenNotifications}
        onBusinessChange={chooseContactBusiness}
        onOpenContact={openContact}
        onContactUpdated={phoneE164 => navigation.replace({ contactPhoneE164: phoneE164 })}
        onBack={() => navigation.dismiss({ contactId: null, contactBusinessId: null, contactPhoneE164: null })}
        onClose={() => navigation.dismiss({ contactsOpen: false, contactId: null, contactBusinessId: null, contactPhoneE164: null })}
        onOpenSettings={openSettings}
        onOpenNotifications={openNotifications}
        onMessage={openComposerForContact}
        onCall={openCallForContact}
      /> : lookupOpen && snapshot.features?.lookup ? <LookupPage businessId={businessId} lookupId={lookupId} onLookupOpen={id => navigation.push({ lookupOpen: true, lookupId: id })} onLookupBack={() => navigation.dismiss({ lookupOpen: true, lookupId: null })} onClose={() => navigation.dismiss({ lookupOpen: false, lookupId: null })} /> : <>
      <main className="activity-pane">
        <header className="mobile-header">
          <BrandMark compact />
          <button className="mobile-business-select" onClick={openBusinessPicker}><span>{selectedBusiness?.name ?? 'All lines'}</span><ChevronDown size={15} /></button>
          <div className="mobile-header-utilities">
            <button className="icon-button" aria-label="Open settings" onClick={openSettings}><Settings size={18} /></button>
            <button className="icon-button notification-trigger" aria-label={`Notifications${unseenNotifications ? `, ${unseenNotifications} unseen` : ''}`} onClick={openNotifications}>
              <Bell size={18} />
              {unseenNotifications > 0 && <span className="notification-count" aria-hidden="true">{unseenNotifications > 9 ? '9+' : unseenNotifications}</span>}
            </button>
          </div>
        </header>

        <div className="activity-header">
          <div className="title-row">
            <div>
              <p className="eyebrow">Communications desk</p>
              <h1>{selectedBusiness?.name ?? 'All communications'}</h1>
              <p className="subtitle">{selectedBusiness ? `${selectedBusiness.phoneNumber} · ${selectedBusiness.todayCount} interactions today` : 'Every call and message, one accountable timeline.'}</p>
            </div>
            <div className="header-actions">
              <button className="icon-button desktop-notification-trigger" aria-label="Open settings" onClick={openSettings}><Settings size={18} /></button>
              <button className="icon-button notification-trigger desktop-notification-trigger" aria-label={`Notifications${unseenNotifications ? `, ${unseenNotifications} unseen` : ''}`} onClick={openNotifications}>
                <Bell size={18} />
                {unseenNotifications > 0 && <span className="notification-count" aria-hidden="true">{unseenNotifications > 9 ? '9+' : unseenNotifications}</span>}
              </button>
              <button className="secondary-action" onClick={() => openComposer()}><MessageSquareText size={17} /> New message</button>
              <button className="primary-action" onClick={() => openCall()}><PhoneCall size={17} /> Call out</button>
            </div>
          </div>

          <section className="queue-overview" aria-label="Today's communications queues">
            <QueueSummary icon={MessageCircleMore} label="New messages" value={snapshot.totals.newMessages} businesses={businesses} countFor={line => line.newMessageCount} tone="new" onOpen={lineId => chooseQueue('new', lineId)} />
            <QueueSummary icon={PhoneCall} label="Calls taken" value={snapshot.totals.callsTaken} businesses={businesses} countFor={line => line.callsTakenCount} tone="calls" onOpen={lineId => chooseQueue('calls', lineId)} />
            <QueueSummary icon={AlertTriangle} label="Needs review" value={snapshot.totals.needsReview} businesses={businesses} countFor={line => line.needsReviewCount} tone="review" onOpen={lineId => chooseQueue('review', lineId)} />
            <MobileQueueBoard businesses={businesses} totals={snapshot.totals} onOpen={chooseQueue} />
            <div className={`sync-state ${snapshot.providerState || ''}`}><Wifi size={14} /><span>{syncLabel}</span></div>
          </section>

          <div className="toolbar">
            <label className="search-box">
              <Search size={17} />
              <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search people, numbers or conversations" aria-label="Search communications" />
              {query && <button onClick={() => setQuery('')} aria-label="Clear search"><X size={15} /></button>}
            </label>
            <div className="filter-tabs" role="tablist" aria-label="Activity filters">
              {activityTabs.map(item => <button key={item} role="tab" aria-selected={filter === item || (item === 'messages' && filter === 'new')} className={filter === item || (item === 'messages' && filter === 'new') ? 'active' : ''} onClick={() => chooseFilter(item)}>{filterLabels[item]}</button>)}
            </div>
          </div>
        </div>

        <div className="activity-content">
          <div className="timeline-heading"><span>{filter === 'messages' || filter === 'new' ? 'Conversations' : 'Today'}</span><small>{visibleEvents.length} {filter === 'messages' || filter === 'new' ? 'threads' : 'interactions'}</small></div>
          <div className="activity-list">
            {visibleEvents.map(event => {
              const phone = event.contact.phoneE164 || event.contact.phoneNumber;
              const threadCount = event.kind === 'message' ? messageThreadCounts.get(`${event.businessId}:${phone}`) || 1 : undefined;
              return <ActivityCard key={event.id} event={event} business={businesses.find(item => item.id === event.businessId)!} threadCount={threadCount} active={event.id === selectedId} onClick={() => chooseEvent(event.id)} />;
            })}
            {!visibleEvents.length && <EmptyState onReset={() => navigation.push({ query: '', filter: 'all', businessId: 'all', mobileDetailOpen: false })} onMessage={() => openComposer()} />}
          </div>
        </div>
      </main>

      <aside className="detail-pane">
        {selectedEvent ? <CommunicationDetail key={selectedEvent.id} event={selectedEvent.kind === 'call' && callDetail?.id === selectedEvent.id ? callDetail : selectedEvent} business={businesses.find(item => item.id === selectedEvent.businessId)!} conversation={conversation} conversationLoading={conversationLoading} onMessage={() => openComposer(selectedEvent)} onCall={() => openCall(selectedEvent)} onLookup={() => void openLookupForEvent(selectedEvent)} onLookupSaved={() => void refreshDashboard(true)} onEditContact={() => openContactNameEditor(selectedEvent)} onDismissReview={() => void dismissNeedsReview(selectedEvent.kind, selectedEvent.id)} reviewDismissing={reviewDismissingId === selectedEvent.id} onDeleteMessage={message => void deleteMessageFromLineWatch(message)} onDeleteConversation={selectedEvent.kind === 'message' ? () => void deleteConversationFromLineWatch(selectedEvent) : undefined} onDeleteCall={selectedEvent.kind === 'call' ? () => void deleteCallFromLineWatch(selectedEvent) : undefined} deletingMessageId={messageDeletingId} conversationDeleting={conversationDeleting} callDeleting={callDeletingId === selectedEvent.id} /> : <NoSelection onMessage={() => openComposer()} />}
      </aside>
      </>}

      {mobileDetailOpen && selectedEvent && (
        <div className="mobile-detail" role="dialog" aria-modal="true" aria-label="Communication details">
          <button className="mobile-back" onClick={() => navigation.dismiss({ mobileDetailOpen: false })}><ChevronLeft size={18} /> Activity</button>
          <CommunicationDetail key={selectedEvent.id} event={selectedEvent.kind === 'call' && callDetail?.id === selectedEvent.id ? callDetail : selectedEvent} business={businesses.find(item => item.id === selectedEvent.businessId)!} conversation={conversation} conversationLoading={conversationLoading} onMessage={() => openComposer(selectedEvent)} onCall={() => openCall(selectedEvent)} onLookup={() => void openLookupForEvent(selectedEvent)} onLookupSaved={() => void refreshDashboard(true)} onEditContact={() => openContactNameEditor(selectedEvent)} onDismissReview={() => void dismissNeedsReview(selectedEvent.kind, selectedEvent.id)} reviewDismissing={reviewDismissingId === selectedEvent.id} onDeleteMessage={message => void deleteMessageFromLineWatch(message)} onDeleteConversation={selectedEvent.kind === 'message' ? () => void deleteConversationFromLineWatch(selectedEvent) : undefined} onDeleteCall={selectedEvent.kind === 'call' ? () => void deleteCallFromLineWatch(selectedEvent) : undefined} deletingMessageId={messageDeletingId} conversationDeleting={conversationDeleting} callDeleting={callDeletingId === selectedEvent.id} />
        </div>
      )}

      {businessPickerOpen && <BusinessPicker businesses={businesses} businessId={businessId} onChoose={chooseBusiness} onClose={() => navigation.dismiss({ businessPickerOpen: false })} />}
      {notificationsOpen && <NotificationCenter notifications={notifications} loading={notificationsLoading} dismissingId={reviewDismissingId} clearingId={notificationClearingId} onOpen={openNotificationEvent} onDismiss={notification => void dismissNeedsReview(notification.kind, notification.communicationId)} onClear={notification => void clearNotification(notification)} onClose={() => navigation.dismiss({ notificationsOpen: false })} />}
      {composeOpen && <MessageComposer businesses={businesses} businessId={composeBusinessId} to={composeTo} text={composeText} replyContext={composeReplyContext} sending={sendState === 'sending'} error={sendError} onBusinessChange={setComposeBusinessId} onToChange={setComposeTo} onTextChange={setComposeText} onClose={() => navigation.dismiss({ composeOpen: false })} onSend={sendMessage} />}
      {callOpen && <CallComposer businesses={businesses} businessId={callBusinessId} to={callTo} settings={snapshot.callSettings} state={callState} call={activeCall} error={callError} onBusinessChange={setCallBusinessId} onToChange={setCallTo} onClose={() => navigation.dismiss({ callOpen: false })} onStart={() => void startCall()} />}
      {settingsOpen && <SettingsPanel businesses={businesses} activation={snapshot.activation} onClose={() => navigation.dismiss({ settingsOpen: false })} onColor={updateBusinessColor} onLogo={uploadBusinessLogo} onRemoveLogo={removeBusinessLogo} />}
      {contactNameEditor && <ContactNameDialog target={contactNameEditor} onClose={() => setContactNameEditor(null)} onSave={saveContactName} />}
      {toast && <div className="toast" role="status"><Sparkles size={16} />{toast}</div>}
      </div>
      {createPortal(
        <nav className="mobile-nav" aria-label="Primary navigation">
          <MobileNavButton icon={LayoutDashboard} label="Activity" active={!contactsOpen && !lookupOpen && filter === 'all'} onClick={() => chooseFilter('all')} />
          <MobileNavButton icon={MessageCircleMore} label="Messages" active={!contactsOpen && !lookupOpen && (filter === 'messages' || filter === 'new')} onClick={() => chooseFilter('messages')} />
          <MobileNavButton icon={Phone} label="Calls" active={!contactsOpen && !lookupOpen && filter === 'calls'} onClick={() => chooseFilter('calls')} />
          {snapshot.features?.lookup ? <MobileNavButton icon={ScanSearch} label="Lookup" active={lookupOpen} onClick={openLookup} /> : <MobileNavButton icon={AlertTriangle} label="Review" active={!contactsOpen && filter === 'review'} onClick={() => chooseFilter('review')} />}
          <MobileNavButton icon={Users} label="Contacts" active={contactsOpen} onClick={openContacts} />
        </nav>,
        document.body,
      )}
    </>
  );
}

function BrandMark({ compact = false }: { compact?: boolean }) {
  return <div className={`brand ${compact ? 'compact' : ''}`}><span className="brand-symbol"><span /><span /><span /></span>{!compact && <span className="brand-copy"><strong>LineWatch</strong><small>Communications console</small></span>}</div>;
}

function BusinessAvatar({ business }: { business: BusinessLine }) {
  return <span className={`business-avatar ${business.logoUrl ? 'has-logo' : ''}`} style={{ color: business.color, background: business.softColor }}>{business.logoUrl ? <img src={business.logoUrl} alt="" /> : business.initials}</span>;
}

function BusinessButton({ business, active, onClick }: { business: BusinessLine; active: boolean; onClick: () => void }) {
  return <button className={`business-button ${active ? 'active' : ''}`} onClick={onClick}><BusinessAvatar business={business} /><span className="business-copy"><strong>{business.name}</strong><small>{business.phoneNumber}</small></span><CountBadge count={business.unreadCount} /></button>;
}

function CountBadge({ count }: { count: number }) {
  return count > 0 ? <span className="count-badge">{count}</span> : null;
}

function QueueSummary({ icon: Icon, label, value, businesses, countFor, tone, onOpen }: {
  icon: typeof MessageCircleMore;
  label: string;
  value: number;
  businesses: BusinessLine[];
  countFor: (business: BusinessLine) => number;
  tone: 'new' | 'calls' | 'review';
  onOpen: (businessId: string) => void;
}) {
  return (
    <article className={`queue-summary ${tone}`}>
      <button className="queue-total" onClick={() => onOpen('all')} aria-label={`Open ${label.toLowerCase()}`}>
        <span className="queue-icon"><Icon size={16} /></span>
        <span><small>{label}</small><strong>{value}</strong></span>
        <ChevronRight size={15} />
      </button>
      <div className="queue-businesses" aria-label={`${label} by business`}>
        {businesses.map(business => (
          <button key={business.id} onClick={() => onOpen(business.id)} aria-label={`${business.name}: ${countFor(business)} ${label.toLowerCase()}`}>
            <i style={{ background: business.color }} />
            <span>{business.shortName}</span>
            <strong>{countFor(business)}</strong>
          </button>
        ))}
      </div>
    </article>
  );
}

function MobileQueueBoard({ businesses, totals, onOpen }: {
  businesses: BusinessLine[];
  totals: DashboardSnapshot['totals'];
  onOpen: (filter: ActivityFilter, businessId?: string) => void;
}) {
  const activeBusinesses = businesses.filter(line => line.newMessageCount || line.callsTakenCount || line.needsReviewCount);
  return (
    <div className="mobile-queue-board">
      <div className="mobile-queue-totals">
        <button className="new" onClick={() => onOpen('new', 'all')}><MessageCircleMore size={16} /><strong>{totals.newMessages}</strong><span>New messages</span></button>
        <button className="calls" onClick={() => onOpen('calls', 'all')}><PhoneCall size={16} /><strong>{totals.callsTaken}</strong><span>Calls taken</span></button>
        <button className="review" onClick={() => onOpen('review', 'all')}><AlertTriangle size={16} /><strong>{totals.needsReview}</strong><span>Needs review</span></button>
      </div>
      {activeBusinesses.length > 0 && (
        <div className="mobile-queue-lines" aria-label="Queue totals by business">
          {activeBusinesses.map(line => (
            <div key={line.id}>
              <span className="mobile-line-name"><i style={{ background: line.color }} />{line.shortName}</span>
              <button onClick={() => onOpen('new', line.id)} aria-label={`${line.name}: ${line.newMessageCount} new messages`}>{line.newMessageCount}</button>
              <button onClick={() => onOpen('calls', line.id)} aria-label={`${line.name}: ${line.callsTakenCount} calls taken`}>{line.callsTakenCount}</button>
              <button onClick={() => onOpen('review', line.id)} aria-label={`${line.name}: ${line.needsReviewCount} items needing review`}>{line.needsReviewCount}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityCard({ event, business, threadCount, active, onClick }: { event: CommunicationEvent; business: BusinessLine; threadCount?: number; active: boolean; onClick: () => void }) {
  const isMessage = event.kind === 'message';
  const EventIcon = isMessage ? MessageSquareText : event.status === 'missed' ? PhoneMissed : PhoneCall;
  const DirectionIcon = event.direction === 'inbound' ? ArrowDownLeft : ArrowUpRight;
  const preview = isMessage ? event.body || 'Media message' : event.transcriptPreview || (event.status === 'missed' ? 'No answer — callback may be needed.' : 'Call details are ready.');
  const hasResolvedName = hasContactName(event);
  return (
    <button className={`activity-card ${active ? 'active' : ''} ${event.unread ? 'unread' : ''}`} onClick={onClick}>
      <span className="event-icon" style={{ color: business.color, background: business.softColor }}><EventIcon size={18} /></span>
      <span className="event-main">
        <span className="event-topline"><strong><span>{event.contact.name}</span>{hasResolvedName && <small>{event.contact.phoneNumber}</small>}</strong><span className="event-time">{isMessage && event.unread && <i className="unread-dot" aria-label="Unread message" />}<time>{event.displayTime}</time></span></span>
        <span className="event-meta"><span className="business-dot" style={{ background: business.color }} />{business.shortName}<span>·</span><DirectionIcon size={13} />{event.direction}{!isMessage && event.status === 'missed' && <span className="missed-label">Missed</span>}</span>
        <span className="event-preview">{preview}</span>
        <span className="event-flags">
          {!isMessage && event.hasRecording && <span><Headphones size={13} /> Recording</span>}
          {!isMessage && event.hasTranscript && <span><Sparkles size={13} /> Transcript</span>}
          {isMessage && event.mediaCount ? <span>{event.mediaCount} attachments</span> : null}
          {isMessage && threadCount && threadCount > 1 ? <span><MessageCircleMore size={13} /> {threadCount} messages</span> : null}
          {isMessage && event.direction === 'outbound' && <span><Check size={13} /> {event.status}</span>}
          {event.needsReview && <span className="review-flag"><AlertTriangle size={13} /> Needs review</span>}
        </span>
      </span>
      <ChevronRight className="row-chevron" size={17} />
    </button>
  );
}

function hasContactName(event: CommunicationEvent) {
  return event.contact.name.replace(/\D/g, '') !== event.contact.phoneNumber.replace(/\D/g, '');
}

function CommunicationDetail({ event, business, conversation, conversationLoading, onMessage, onCall, onLookup, onLookupSaved, onEditContact, onDismissReview, reviewDismissing, onDeleteMessage, onDeleteConversation, onDeleteCall, deletingMessageId, conversationDeleting, callDeleting }: {
  event: CommunicationEvent;
  business: BusinessLine;
  conversation: Conversation | null;
  conversationLoading: boolean;
  onMessage: () => void;
  onCall: () => void;
  onLookup: () => void;
  onLookupSaved: () => void;
  onEditContact: () => void;
  onDismissReview: () => void;
  reviewDismissing: boolean;
  onDeleteMessage: (message: MessageEvent) => void;
  onDeleteConversation?: () => void;
  onDeleteCall?: () => void;
  deletingMessageId: string | null;
  conversationDeleting: boolean;
  callDeleting: boolean;
}) {
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const duration = event.kind === 'call' ? formatDuration(event.durationSeconds) : null;
  const readyRecording = event.kind === 'call' ? event.recordings?.find(recording => recording.status === 'ready' && recording.url) : undefined;
  const pendingRecording = event.kind === 'call' ? event.recordings?.find(recording => recording.status === 'pending') : undefined;
  const failedRecording = event.kind === 'call' ? event.recordings?.find(recording => recording.status === 'failed') : undefined;
  return (
    <div className="detail-card">
      <div className="detail-toolbar"><span>{event.kind === 'message' ? 'Conversation details' : 'Interaction details'}</span>{event.kind === 'message' && onDeleteConversation ? <button className="icon-button delete-conversation-button" aria-label="Delete conversation from LineWatch" title="Delete conversation from LineWatch" onClick={onDeleteConversation} disabled={conversationDeleting || Boolean(deletingMessageId)}>{conversationDeleting ? <Loader2 className="spinning" size={17} /> : <Trash2 size={17} />}</button> : event.kind === 'call' && onDeleteCall ? <button className="icon-button delete-conversation-button" aria-label="Delete call from LineWatch" title="Delete call from LineWatch" onClick={onDeleteCall} disabled={callDeleting}>{callDeleting ? <Loader2 className="spinning" size={17} /> : <Trash2 size={17} />}</button> : <button className="icon-button" aria-label="More options"><MoreHorizontal size={18} /></button>}</div>
      <div className="contact-hero"><span className="contact-avatar">{event.contact.initials}</span><div className="contact-hero-copy"><h2>{event.contact.name}</h2><p>{event.contact.company || event.contact.phoneNumber}</p>{event.contact.company && <small>{event.contact.phoneNumber}</small>}</div><button type="button" className="contact-name-edit" onClick={onEditContact}><Pencil size={14} />{hasContactName(event) ? 'Edit name' : 'Add name'}</button></div>
      <div className="detail-actions">
        <button onClick={onMessage}><MessageCircleMore size={18} /><span>Message</span><small>Ready</small></button>
        <button onClick={onCall}><PhoneCall size={18} /><span>Call</span><small>Phone bridge</small></button>
        {event.kind === 'message' ? <MessageLookup event={event} onSaved={onLookupSaved} /> : <button className="lookup-contact-action" onClick={onLookup}><ScanSearch size={18} /><span>Lookup caller</span><small>Identity & property intelligence</small></button>}
      </div>
      <div className="identity-card"><BusinessAvatar business={business} /><div><small>{event.direction === 'outbound' ? 'Sent from' : 'Received on'}</small><strong>{business.name}</strong><span>{business.phoneNumber}</span></div><span className="ready-pill"><i /> Ready</span></div>

      {event.needsReview && <section className="review-resolution-card" aria-label="Needs attention">
        <span className="review-resolution-icon"><AlertTriangle size={17} /></span>
        <div><strong>Needs attention</strong><p>{event.reviewReason || 'This interaction was escalated for a human review.'}</p></div>
        <button onClick={onDismissReview} disabled={reviewDismissing}>{reviewDismissing ? <Loader2 className="spinning" size={14} /> : <CheckCircle2 size={15} />}{reviewDismissing ? 'Dismissing…' : 'Dismiss'}</button>
      </section>}

      {event.kind === 'message' ? (
        <section className="detail-section conversation-section">
          <SectionHeading title="Conversation" aside={conversation ? `${conversation.messages.length} messages` : event.status} />
          <button className="reply-button conversation-reply-button" onClick={onMessage}><MessageSquareText size={16} /> Reply from {business.shortName}</button>
          {conversationLoading ? <div className="conversation-loading"><Loader2 size={18} /> Loading thread</div> : <ConversationThread messages={conversation?.messages || [event]} onDelete={onDeleteMessage} deletingMessageId={deletingMessageId} />}
        </section>
      ) : (
        <>
          <section className="detail-section call-summary">
            <SectionHeading title={event.status === 'voicemail' ? 'Voicemail' : 'Call summary'} aside={duration || event.status} />
            <div className="call-facts"><span><Clock3 size={15} /><small>Started</small><strong>{event.displayTime} ago</strong></span><span><Phone size={15} /><small>Direction</small><strong>{capitalize(event.direction)}</strong></span><span><Check size={15} /><small>Outcome</small><strong>{capitalize(event.status)}</strong></span></div>
          </section>
          {event.hasRecording && <section className="detail-section"><SectionHeading title="Recording" aside={readyRecording ? 'Stored privately' : 'Processing'} />{readyRecording ? <audio className="recording-audio" controls preload="metadata" src={readyRecording.url}>Your browser cannot play this recording.</audio> : <div className={`recording-state ${failedRecording ? 'failed' : ''}`}>{pendingRecording ? <Loader2 className="spinning" size={17} /> : <AlertTriangle size={17} />}<span>{pendingRecording ? 'Archiving the Telnyx recording to private storage…' : failedRecording ? 'This recording could not be archived.' : 'Recording metadata is available in review mode; live audio will appear after Telnyx activation.'}</span></div>}</section>}
          {event.hasTranscript && <section className="detail-section transcript-card"><SectionHeading title={transcriptOpen ? 'Full transcript' : 'Transcript preview'} aside={event.transcript?.segments.length ? `${event.transcript.segments.length} segments` : 'Captured text'} />{transcriptOpen && event.transcript ? <div className="full-transcript">{event.transcript.segments.length ? event.transcript.segments.map(segment => <div className="transcript-segment" key={segment.id}><span>{segment.speaker || 'Speaker'}</span><p>{segment.text}</p>{typeof segment.confidence === 'number' && <small>{Math.round(segment.confidence * 100)}% confidence</small>}</div>) : <p>{event.transcript.text}</p>}</div> : <div className="speaker-row"><span>Preview</span><p>{event.transcriptPreview || event.transcript?.text}</p></div>}<button onClick={() => setTranscriptOpen(value => !value)}>{transcriptOpen ? 'Show preview' : 'Open full transcript'} <ChevronRight className={transcriptOpen ? 'rotated' : ''} size={15} /></button></section>}
        </>
      )}

      <section className="detail-section audit-card"><SectionHeading title="Audit trail" aside="Provider preserved" /><div className="audit-row"><span className="audit-check"><Check size={12} /></span><div><strong>Captured by LineWatch</strong><small>Provider event stored with original timestamp</small></div></div><div className="audit-row"><span className="audit-check"><ShieldCheck size={12} /></span><div><strong>Private workspace</strong><small>Deleting here never deletes the Telnyx copy</small></div></div></section>
    </div>
  );
}

function ConversationThread({ messages, onDelete, deletingMessageId }: { messages: MessageEvent[]; onDelete: (message: MessageEvent) => void; deletingMessageId: string | null }) {
  const threadRef = useRef<HTMLDivElement>(null);
  const newestFirst = useMemo(() => [...messages].sort((left, right) => {
    const difference = Date.parse(right.occurredAt) - Date.parse(left.occurredAt);
    return Number.isFinite(difference) ? difference : 0;
  }), [messages]);
  const newestMessageId = newestFirst[0]?.id;

  useEffect(() => {
    threadRef.current?.scrollTo({ top: 0 });
  }, [newestMessageId]);

  return <div ref={threadRef} className="conversation-thread" role="region" aria-label="Conversation messages, newest first" tabIndex={0}>{newestFirst.map(message => <div key={message.id} className={`thread-row ${message.direction}`}><div className="thread-bubble">{message.body && <p>{message.body}</p>}{message.media?.map(media => media.url && media.contentType?.startsWith('image/') ? <a key={media.id} href={media.url} target="_blank" rel="noreferrer"><img src={media.url} alt="Message attachment" /></a> : media.url && media.status === 'ready' ? <a key={media.id} className="thread-file-attachment" href={media.url} target="_blank" rel="noreferrer"><Paperclip size={14} /> Open attachment</a> : <span key={media.id} className="media-status">{media.status === 'failed' ? 'Attachment unavailable' : 'Archiving attachment…'}</span>)}<span className="thread-meta"><small>{message.displayTime} · {message.status}</small><button type="button" aria-label={`Delete message from ${message.displayTime} from LineWatch`} title="Delete from LineWatch" onClick={() => onDelete(message)} disabled={Boolean(deletingMessageId)}>{deletingMessageId === message.id ? <Loader2 className="spinning" size={12} /> : <Trash2 size={12} />}</button></span></div></div>)}</div>;
}

function ContactNameDialog({ target, onClose, onSave }: {
  target: ContactNameEditorTarget;
  onClose: () => void;
  onSave: (displayName: string) => Promise<void>;
}) {
  const currentNameIsPhone = target.displayName.replace(/\D/g, '') === target.phoneNumber.replace(/\D/g, '');
  const [displayName, setDisplayName] = useState(currentNameIsPhone ? '' : target.displayName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, saving]);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = displayName.trim();
    if (!name) {
      setError('Enter the name you want shown for this number.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(name);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'The contact name could not be saved.');
      setSaving(false);
    }
  };

  return (
    <div className="compose-backdrop" onMouseDown={() => { if (!saving) onClose(); }}>
      <form className="compose-sheet contact-name-sheet" role="dialog" aria-modal="true" aria-labelledby="contact-name-title" onMouseDown={event => event.stopPropagation()} onSubmit={submit}>
        <span className="sheet-handle" />
        <div className="compose-heading">
          <div><p className="eyebrow">Contact identity</p><h2 id="contact-name-title">Name this contact</h2><p>This manual name will be used across LineWatch instead of an uncertain lookup result.</p></div>
          <button type="button" className="icon-button" onClick={onClose} disabled={saving} aria-label="Close contact name editor"><X size={18} /></button>
        </div>
        <div className="contact-name-phone"><Phone size={16} /><span><small>Phone number</small><strong>{target.phoneNumber}</strong></span></div>
        <label className="compose-field"><span>Contact name</span><input value={displayName} onChange={event => { setDisplayName(event.target.value); setError(''); }} maxLength={120} autoComplete="name" autoFocus placeholder="First and last name" disabled={saving} /></label>
        {error && <div className="compose-error" role="alert"><AlertTriangle size={16} />{error}</div>}
        <div className="compose-note"><ShieldCheck size={16} /><span>Manual names have priority over CNAM and automated enrichment, so weak lookups will not overwrite your change.</span></div>
        <div className="compose-actions"><button type="button" className="cancel-compose" onClick={onClose} disabled={saving}>Cancel</button><button type="submit" className="send-message" disabled={saving || !displayName.trim()}>{saving ? <Loader2 className="spinning" size={17} /> : <Check size={17} />}{saving ? 'Saving…' : 'Save name'}</button></div>
      </form>
    </div>
  );
}

function MessageComposer({ businesses, businessId, to, text, replyContext, sending, error, onBusinessChange, onToChange, onTextChange, onClose, onSend }: {
  businesses: BusinessLine[];
  businessId: string;
  to: string;
  text: string;
  replyContext: MessageReplyContext | null;
  sending: boolean;
  error: string;
  onBusinessChange: (value: string) => void;
  onToChange: (value: string) => void;
  onTextChange: (value: string) => void;
  onClose: () => void;
  onSend: (payload: ComposerSendPayload) => Promise<ComposerSendResult>;
}) {
  const selected = businesses.find(line => line.id === businessId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachment, setAttachment] = useState<File>();
  const [stagedAttachment, setStagedAttachment] = useState<MessageAttachmentUpload>();
  const [attachmentError, setAttachmentError] = useState('');
  const [recipientMode, setRecipientMode] = useState<'single' | 'multiple'>('single');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientEntry, setRecipientEntry] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [contacts, setContacts] = useState<ContactDirectoryItem[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateSaveOpen, setTemplateSaveOpen] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateStatus, setTemplateStatus] = useState('');
  const [reviewingBatch, setReviewingBatch] = useState(false);
  const [replyPreviewVisible, setReplyPreviewVisible] = useState(true);
  const previewUrl = useMemo(() => attachment?.type.startsWith('image/') ? URL.createObjectURL(attachment) : undefined, [attachment]);
  const activeAttachment = attachment ? { name: attachment.name, contentType: attachment.type, byteSize: attachment.size } : stagedAttachment;
  const replyMatchesRecipient = replyContext
    && replyContext.businessId === businessId
    && replyContext.phoneNumber.replace(/\D/g, '') === to.replace(/\D/g, '');
  const replyPreview = replyContext?.body.trim()
    || (replyContext?.mediaCount === 1 ? 'Photo or file attachment' : replyContext?.mediaCount ? `${replyContext.mediaCount} attachments` : 'Message content unavailable');

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    let cancelled = false;
    setTemplatesLoading(true);
    setTemplateStatus('');
    communicationsRepository.getMessageTemplates(businessId)
      .then(items => { if (!cancelled) setTemplates(items); })
      .catch(reason => { if (!cancelled) setTemplateStatus(reason instanceof Error ? reason.message : 'Templates could not be loaded.'); })
      .finally(() => { if (!cancelled) setTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, [businessId]);

  useEffect(() => {
    if (recipientMode !== 'multiple') return;
    let cancelled = false;
    setContactsLoading(true);
    const timer = window.setTimeout(() => {
      communicationsRepository.getContacts(businessId, contactQuery)
        .then(result => { if (!cancelled) setContacts(result.data.slice(0, 40)); })
        .catch(() => { if (!cancelled) setContacts([]); })
        .finally(() => { if (!cancelled) setContactsLoading(false); });
    }, 180);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [businessId, contactQuery, recipientMode]);

  useEffect(() => setReviewingBatch(false), [businessId, text, attachment, stagedAttachment, recipients.length]);

  const chooseAttachment = (file?: File) => {
    if (!file) return;
    const normalizedFile = normalizeMmsFile(file);
    setAttachmentError('');
    if (!MMS_DIRECT_TYPES.has(normalizedFile.type) && !normalizedFile.type.startsWith('image/')) {
      setAttachment(undefined);
      setAttachmentError('Choose a supported photo, video, audio, PDF, contact card, or calendar file.');
      return;
    }
    if (!normalizedFile.type.startsWith('image/') && normalizedFile.size > MMS_SAFE_MAX_BYTES) {
      setAttachment(undefined);
      setAttachmentError('This file is larger than the reliable MMS limit of 600 KB.');
      return;
    }
    setStagedAttachment(undefined);
    setAttachment(normalizedFile);
  };

  const removeAttachment = () => {
    setAttachment(undefined);
    setStagedAttachment(undefined);
    setAttachmentError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const changeBusiness = (value: string) => {
    onBusinessChange(value);
    setRecipients([]);
    setContactQuery('');
    setTemplatesOpen(false);
    setTemplateSaveOpen(false);
    setAttachment(undefined);
    setStagedAttachment(undefined);
    setTemplateStatus('');
  };

  const switchRecipientMode = (mode: 'single' | 'multiple') => {
    setRecipientMode(mode);
    setReviewingBatch(false);
    if (mode === 'multiple' && to.trim()) setRecipients(current => current.includes(to.trim()) ? current : [to.trim(), ...current].slice(0, 25));
  };

  const toggleRecipient = (phone: string) => {
    setRecipients(current => current.includes(phone) ? current.filter(item => item !== phone) : current.length < 25 ? [...current, phone] : current);
  };

  const addRecipient = () => {
    const value = recipientEntry.trim();
    if (!value || recipients.includes(value) || recipients.length >= 25) return;
    setRecipients(current => [...current, value]);
    setRecipientEntry('');
  };

  const applyTemplate = async (templateId: string) => {
    setTemplateBusy(true);
    setTemplateStatus('');
    try {
      const applied = await communicationsRepository.applyMessageTemplate(templateId);
      onTextChange(applied.template.text);
      setAttachment(undefined);
      setStagedAttachment(applied.media[0]);
      setTemplatesOpen(false);
      setTemplateStatus(`Applied “${applied.template.name}”.`);
    } catch (reason) {
      setTemplateStatus(reason instanceof Error ? reason.message : 'The template could not be applied.');
    } finally {
      setTemplateBusy(false);
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim() || (!text.trim() && !activeAttachment)) return;
    setTemplateBusy(true);
    setTemplateStatus('');
    try {
      let uploaded = stagedAttachment;
      if (attachment) uploaded = await communicationsRepository.uploadMessageAttachment(await prepareMmsAttachment(attachment));
      const saved = await communicationsRepository.createMessageTemplate({
        businessId,
        name: templateName.trim(),
        text,
        attachments: uploaded ? [{ objectKey: uploaded.objectKey, name: uploaded.name }] : undefined,
      });
      setTemplates(current => [saved, ...current]);
      setTemplateName('');
      setTemplateSaveOpen(false);
      setTemplateStatus(`Saved “${saved.name}” for ${selected?.name || 'this line'}.`);
    } catch (reason) {
      setTemplateStatus(reason instanceof Error ? reason.message : 'The template could not be saved.');
    } finally {
      setTemplateBusy(false);
    }
  };

  const deleteTemplate = async (template: MessageTemplate) => {
    if (!window.confirm(`Delete “${template.name}”?`)) return;
    setTemplateBusy(true);
    setTemplateStatus('');
    try {
      await communicationsRepository.deleteMessageTemplate(template.id);
      setTemplates(current => current.filter(item => item.id !== template.id));
      setTemplateStatus(`Deleted “${template.name}”.`);
    } catch (reason) {
      setTemplateStatus(reason instanceof Error ? reason.message : 'The template could not be deleted.');
    } finally {
      setTemplateBusy(false);
    }
  };

  const sendRecipients = recipientMode === 'single' ? [to.trim()].filter(Boolean) : recipients;
  const canSend = Boolean(businessId && sendRecipients.length && (text.trim() || activeAttachment) && (recipientMode === 'single' || sendRecipients.length >= 2));
  const submit = async () => {
    if (recipientMode === 'multiple' && !reviewingBatch) {
      setReviewingBatch(true);
      return;
    }
    const result = await onSend({ recipients: sendRecipients, attachment, stagedAttachment });
    if (result?.failedRecipients.length) {
      if (result.failedRecipients.length === 1) {
        onToChange(result.failedRecipients[0]!);
        setRecipientMode('single');
        setRecipients([]);
      } else {
        setRecipients(result.failedRecipients);
      }
      setReviewingBatch(false);
    }
  };

  return (
    <div className="sheet-backdrop compose-backdrop" onClick={onClose}>
      <section className="compose-sheet" role="dialog" aria-modal="true" aria-label={replyMatchesRecipient ? 'Reply to text message' : 'New text message'} onClick={event => event.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="compose-heading"><div><p className="eyebrow">Outbound message</p><h2>{replyMatchesRecipient ? 'Reply to conversation' : 'Start a conversation'}</h2><p>{replyMatchesRecipient ? 'Review the previous message, then write your response below.' : 'Choose exactly which business number the customer will see.'}</p></div><button className="icon-button" onClick={onClose} aria-label="Close composer"><X size={18} /></button></div>
        <label className="compose-field"><span>Send from</span><select value={businessId} onChange={event => changeBusiness(event.target.value)}>{businesses.map(line => <option key={line.id} value={line.id}>{line.name} — {line.phoneNumber}</option>)}</select></label>
        <div className="recipient-mode" role="group" aria-label="Recipient mode">
          <button type="button" className={recipientMode === 'single' ? 'active' : ''} onClick={() => switchRecipientMode('single')}>One person</button>
          <button type="button" className={recipientMode === 'multiple' ? 'active' : ''} onClick={() => switchRecipientMode('multiple')}><Users size={14} /> Multiple people</button>
        </div>
        {recipientMode === 'single' ? <label className="compose-field"><span>Mobile number</span><input type="tel" inputMode="tel" value={to} onChange={event => onToChange(event.target.value)} placeholder="(801) 555-0123" autoComplete="tel" /></label> : <div className="bulk-recipient-panel">
          <div className="bulk-recipient-heading"><span>Recipients</span><strong>{recipients.length} / 25</strong></div>
          {recipients.length > 0 && <div className="recipient-chips">{recipients.map(phone => <button type="button" key={phone} onClick={() => toggleRecipient(phone)} aria-label={`Remove ${phone}`}>{phone}<X size={12} /></button>)}</div>}
          <div className="recipient-add-row"><input type="tel" inputMode="tel" value={recipientEntry} onChange={event => setRecipientEntry(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); addRecipient(); } }} placeholder="Add a mobile number" /><button type="button" onClick={addRecipient} disabled={!recipientEntry.trim() || recipients.length >= 25}><Plus size={15} /> Add</button></div>
          <label className="composer-contact-search"><Search size={15} /><input value={contactQuery} onChange={event => setContactQuery(event.target.value)} placeholder={`Search ${selected?.shortName || 'line'} contacts`} /></label>
          <div className="recipient-contact-list" role="listbox" aria-label="Choose contacts" aria-multiselectable="true">
            {contactsLoading && <span className="recipient-contact-empty"><Loader2 className="spinning" size={15} /> Loading contacts…</span>}
            {!contactsLoading && !contacts.length && <span className="recipient-contact-empty">No contacts found for this line.</span>}
            {!contactsLoading && contacts.map(contact => {
              const chosen = recipients.includes(contact.phoneE164);
              return <button type="button" role="option" aria-selected={chosen} className={chosen ? 'selected' : ''} key={contact.id} onClick={() => toggleRecipient(contact.phoneE164)} disabled={!chosen && recipients.length >= 25}><span>{contact.initials}</span><span><strong>{contact.displayName}</strong><small>{contact.phoneNumber}</small></span>{chosen ? <Check size={15} /> : <Plus size={15} />}</button>;
            })}
          </div>
          <p className="bulk-privacy-note"><ShieldCheck size={14} /> Every recipient gets a separate private conversation. This never creates a group text.</p>
        </div>}
        {recipientMode === 'single' && replyMatchesRecipient && replyPreviewVisible && replyContext && <aside className={`reply-context-preview ${replyContext.direction}`} aria-label="Previous message">
          <span className="reply-context-icon"><MessageSquareText size={16} /></span>
          <div><span className="reply-context-heading"><strong>Previous message</strong><small>{replyContext.direction === 'inbound' ? replyContext.contactName : 'Sent by you'} · {replyContext.displayTime}</small></span><p>{replyPreview}</p></div>
          <button type="button" onClick={() => setReplyPreviewVisible(false)} aria-label="Hide previous message preview"><X size={14} /></button>
        </aside>}
        <div className="compose-field message-field">
          <div className="message-field-heading"><span>Message</span><span><button type="button" onClick={() => { setTemplatesOpen(open => !open); setTemplateSaveOpen(false); }} disabled={templateBusy}><FileText size={13} /> Templates</button><button type="button" onClick={() => { setTemplateSaveOpen(open => !open); setTemplatesOpen(false); }} disabled={templateBusy || (!text.trim() && !activeAttachment)}><Plus size={13} /> Save current</button></span></div>
          {templatesOpen && <div className="template-panel">
            {templatesLoading && <span className="template-empty"><Loader2 className="spinning" size={15} /> Loading templates…</span>}
            {!templatesLoading && !templates.length && <span className="template-empty">No templates saved for this business line yet.</span>}
            {!templatesLoading && templates.map(template => <div className="template-row" key={template.id}><button type="button" onClick={() => void applyTemplate(template.id)} disabled={templateBusy}><strong>{template.name}</strong><small>{template.text || 'Attachment only'}{template.attachments.length ? ` · ${template.attachments[0]!.name}` : ''}</small></button><button type="button" onClick={() => void deleteTemplate(template)} disabled={templateBusy} aria-label={`Delete ${template.name}`}><Trash2 size={14} /></button></div>)}
          </div>}
          {templateSaveOpen && <div className="template-save-row"><input value={templateName} onChange={event => setTemplateName(event.target.value)} maxLength={80} placeholder="Template name" autoFocus /><button type="button" onClick={() => void saveTemplate()} disabled={templateBusy || !templateName.trim()}>{templateBusy ? <Loader2 className="spinning" size={14} /> : <Check size={14} />} Save</button></div>}
          <div className="message-entry-row">
            <input ref={fileInputRef} className="visually-hidden" type="file" accept={MMS_ACCEPT} tabIndex={-1} aria-hidden="true" onChange={event => chooseAttachment(event.target.files?.[0])} />
            <button type="button" className="message-attachment-button" onClick={() => fileInputRef.current?.click()} disabled={sending} aria-label="Attach a photo or file" title="Attach a photo or file"><Plus size={22} /></button>
            <textarea aria-label="Message" value={text} onChange={event => onTextChange(event.target.value)} placeholder={`Write a message from ${selected?.name || 'this business'}…`} maxLength={5000} rows={6} />
          </div>
          <small>{text.length.toLocaleString()} / 5,000</small>
        </div>
        {activeAttachment && <div className="message-attachment-preview">
          <span className="message-attachment-thumb">{previewUrl ? <img src={previewUrl} alt="Selected attachment preview" /> : <FileText size={19} />}</span>
          <span><strong>{activeAttachment.name}</strong><small>{formatFileSize(activeAttachment.byteSize)}{attachment?.type.startsWith('image/') && attachment.size > MMS_SAFE_MAX_BYTES ? ' · will be optimized' : stagedAttachment ? ' · from saved template' : ''}</small></span>
          <button type="button" onClick={removeAttachment} disabled={sending} aria-label={`Remove ${activeAttachment.name}`}><X size={16} /></button>
        </div>}
        {(attachmentError || error || templateStatus) && <div className={attachmentError || error ? 'compose-error' : 'compose-status'} role="status">{attachmentError || error ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}{attachmentError || error || templateStatus}</div>}
        {recipientMode === 'multiple' && reviewingBatch && <div className="batch-review" role="alert"><Users size={17} /><span><strong>Ready to send {recipients.length} separate messages</strong><small>Confirm once more. Each person receives the same content privately from {selected?.name}.</small></span></div>}
        <div className="compose-note"><ShieldCheck size={16} /><span>Attachments are sent as MMS and privately archived with the conversation. For reliable delivery, LineWatch keeps media within 600 KB.</span></div>
        <div className="compose-actions"><button className="cancel-compose" onClick={onClose} disabled={sending}>Cancel</button><button className="send-message" onClick={() => void submit()} disabled={sending || !canSend}>{sending ? <Loader2 className="spinning" size={17} /> : <Send size={17} />}{sending ? 'Sending…' : recipientMode === 'multiple' ? reviewingBatch ? `Confirm ${recipients.length} sends` : `Review ${recipients.length} sends` : activeAttachment ? 'Send MMS' : 'Send message'}</button></div>
      </section>
    </div>
  );
}

function CallComposer({ businesses, businessId, to, settings, state, call, error, onBusinessChange, onToChange, onClose, onStart }: {
  businesses: BusinessLine[];
  businessId: string;
  to: string;
  settings: DashboardSnapshot['callSettings'];
  state: 'idle' | 'starting' | 'active';
  call: CallEvent | null;
  error: string;
  onBusinessChange: (value: string) => void;
  onToChange: (value: string) => void;
  onClose: () => void;
  onStart: () => void;
}) {
  const selected = businesses.find(line => line.id === businessId);
  const stage = call?.status || (state === 'starting' ? 'starting' : 'ready');
  const terminal = call && ['completed', 'failed', 'cancelled', 'missed'].includes(call.status);
  const stageCopy: Record<string, string> = {
    ready: 'Ready to start',
    starting: 'Starting securely…',
    calling_owner: `Calling your cellphone ${settings?.maskedBridgePhone || ''}`.trim(),
    calling_customer: 'You answered — now calling the customer',
    connecting: 'Customer answered — connecting both calls',
    in_progress: 'Connected — speak normally on your phone',
    completed: 'Call completed and saved to the audit trail',
    failed: call?.bridge?.failureReason || 'The call did not connect',
    cancelled: 'You did not answer, so the customer was not called',
    missed: 'The call was not answered',
  };
  return (
    <div className="sheet-backdrop compose-backdrop" onClick={onClose}>
      <section className="compose-sheet call-sheet" role="dialog" aria-modal="true" aria-label="Call from a business number" onClick={event => event.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="compose-heading"><div><p className="eyebrow">Phone bridge</p><h2>Call from a business number</h2><p>Your normal cellphone handles the conversation. No browser microphone is needed.</p></div><button className="icon-button" onClick={onClose} aria-label="Close call panel"><X size={18} /></button></div>
        {!call && <>
          <label className="compose-field"><span>Customer sees</span><select value={businessId} onChange={event => onBusinessChange(event.target.value)}>{businesses.map(line => <option key={line.id} value={line.id}>{line.name} — {line.phoneNumber}</option>)}</select></label>
          <label className="compose-field"><span>Customer number</span><input type="tel" inputMode="tel" value={to} onChange={event => onToChange(event.target.value)} placeholder="(801) 555-0123" autoComplete="tel" /></label>
        </>}
        <div className="bridge-flow">
          <span className={stage === 'ready' || stage === 'starting' ? 'current' : 'done'}><i>1</i><b>We call you</b><small>{settings?.maskedBridgePhone || 'Cellphone setup needed'}</small></span>
          <span className={['calling_customer', 'connecting'].includes(stage) ? 'current' : ['in_progress', 'completed'].includes(stage) ? 'done' : ''}><i>2</i><b>Then the customer</b><small>Only after you answer</small></span>
          <span className={stage === 'in_progress' ? 'current' : stage === 'completed' ? 'done' : ''}><i>3</i><b>Talk normally</b><small>Caller ID: {selected?.shortName || 'business line'}</small></span>
        </div>
        <div className={`call-stage ${terminal ? 'terminal' : ''}`}><PhoneCall size={18} /><strong>{stageCopy[stage] || capitalize(stage.replace(/_/g, ' '))}</strong></div>
        {error && <div className="compose-error" role="alert"><AlertTriangle size={16} />{error}</div>}
        <div className="compose-note"><ShieldCheck size={16} /><span>The customer is never called unless you answer first. The same protected API can be reused by a future native app.</span></div>
        <div className="compose-actions"><button className="cancel-compose" onClick={onClose}>{terminal ? 'Done' : 'Close'}</button>{!call && <button className="send-message" onClick={onStart} disabled={state === 'starting' || !settings?.enabled || !businessId || !to.trim()}>{state === 'starting' ? <Loader2 className="spinning" size={17} /> : <PhoneCall size={17} />}{state === 'starting' ? 'Calling you…' : 'Call my phone first'}</button>}</div>
      </section>
    </div>
  );
}

function SettingsPanel({ businesses, activation, onClose, onColor, onLogo, onRemoveLogo }: {
  businesses: BusinessLine[];
  activation: DashboardSnapshot['activation'];
  onClose: () => void;
  onColor: (businessId: string, color: string) => Promise<void>;
  onLogo: (businessId: string, file: File) => Promise<void>;
  onRemoveLogo: (businessId: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    setError('');
    try { await action(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'The setting could not be saved.'); }
    finally { setBusy(''); }
  };
  const selectLogo = (business: BusinessLine, file?: File) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setError('Choose a PNG, JPEG, or WebP image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Logos must be 2 MB or smaller.');
      return;
    }
    void run(`logo-${business.id}`, () => onLogo(business.id, file));
  };
  const readiness = [
    { label: 'Dashboard review mode', ready: activation?.mode === 'mock', detail: activation?.mode === 'live' ? 'Live Telnyx mode is active' : 'No carrier routing changes' },
    { label: 'Messaging connection', ready: Boolean(activation?.messagingReady), detail: activation?.messagingReady ? 'Telnyx API key is configured' : 'Telnyx credentials still required' },
    { label: 'Click-to-call bridge', ready: Boolean(activation?.callingReady), detail: activation?.callingReady ? 'Owner-first calling is configured' : 'Connection ID and owner phone required' },
    { label: 'Call recording', ready: Boolean(activation?.recordingEnabled), detail: activation?.recordingEnabled ? 'Private archival is enabled' : 'Disabled in server configuration' },
    { label: 'Call transcription', ready: Boolean(activation?.transcriptionEnabled), detail: activation?.transcriptionEnabled ? 'Transcript capture is enabled' : 'Disabled until approved' },
  ];
  return (
    <div className="sheet-backdrop compose-backdrop" onClick={onClose}>
      <section className="compose-sheet settings-sheet" role="dialog" aria-modal="true" aria-label="LineWatch settings" onClick={event => event.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="compose-heading"><div><p className="eyebrow">Workspace settings</p><h2>LineWatch settings</h2><p>Choose notifications for this device, organize each business identity, and review Telnyx readiness.</p></div><button className="icon-button" onClick={onClose} aria-label="Close settings"><X size={18} /></button></div>
        <PushNotificationSettings businesses={businesses} />
        <section className="settings-section"><div className="settings-heading"><h3>Business identities</h3><span>PNG, JPEG or WebP · 2 MB maximum</span></div><div className="brand-settings-list">{businesses.map(business => <div className="brand-setting" key={business.id}><BusinessAvatar business={business} /><div className="brand-setting-copy"><strong>{business.name}</strong><small>{business.phoneNumber}</small></div><label className="color-picker" title={`Choose ${business.name} color`}><input type="color" value={business.color} disabled={Boolean(busy)} onChange={event => void run(`color-${business.id}`, () => onColor(business.id, event.target.value))} /><span>Color</span></label><label className="brand-upload"><input type="file" accept="image/png,image/jpeg,image/webp" disabled={Boolean(busy)} onChange={event => { selectLogo(business, event.target.files?.[0]); event.currentTarget.value = ''; }} />{busy === `logo-${business.id}` ? <Loader2 className="spinning" size={15} /> : <ImagePlus size={15} />}<span>{business.logoUrl ? 'Replace' : 'Upload'}</span></label>{business.logoUrl && <button className="remove-logo" disabled={Boolean(busy)} onClick={() => void run(`remove-${business.id}`, () => onRemoveLogo(business.id))} aria-label={`Remove ${business.name} logo`}>{busy === `remove-${business.id}` ? <Loader2 className="spinning" size={15} /> : <Trash2 size={15} />}</button>}</div>)}</div></section>
        <section className="settings-section"><div className="settings-heading"><h3>Telnyx activation</h3><span>{activation?.mode === 'live' ? 'Live' : 'Safe review mode'}</span></div><div className="activation-list">{readiness.map(item => <div className={item.ready ? 'ready' : 'waiting'} key={item.label}><span>{item.ready ? <Check size={14} /> : <Clock3 size={14} />}</span><div><strong>{item.label}</strong><small>{item.detail}</small></div></div>)}</div><div className="activation-note"><ShieldCheck size={17} /><p>Activation status is read-only here. Carrier credentials and routing stay server-controlled so a logo or color change can never disrupt phone service.</p></div></section>
        {error && <div className="compose-error" role="alert"><AlertTriangle size={16} />{error}</div>}
        <div className="compose-actions"><button className="send-message" onClick={onClose}>Done</button></div>
      </section>
    </div>
  );
}

const defaultLinePushPreferences: PushCategoryPreferences = { newMessages: true, newCalls: true, needsReview: true };

function preferencesForBusinesses(businesses: BusinessLine[], current?: PushPreferences): PushPreferences {
  return {
    lines: Object.fromEntries(businesses.map(business => [
      business.id,
      current?.lines[business.id] || { ...defaultLinePushPreferences },
    ])),
  };
}

function PushNotificationSettings({ businesses }: { businesses: BusinessLine[] }) {
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const standalone = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
  const ios = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const [configured, setConfigured] = useState(false);
  const [publicKey, setPublicKey] = useState('');
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [preferences, setPreferences] = useState<PushPreferences>(() => preferencesForBusinesses(businesses));
  const [permission, setPermission] = useState<NotificationPermission>(supported ? Notification.permission : 'default');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [diagnostics, setDiagnostics] = useState<PushDeliveryDiagnostics | null>(null);
  const [testing, setTesting] = useState('');
  const [testStatus, setTestStatus] = useState('');
  const businessKey = businesses.map(business => business.id).join('|');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const current = supported ? await (await navigator.serviceWorker.ready).pushManager.getSubscription() : null;
        const [settings, deliveryDiagnostics] = await Promise.all([
          communicationsRepository.getPushSettings(current?.endpoint),
          current ? communicationsRepository.getPushDiagnostics(current.endpoint) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setConfigured(settings.configured);
        setPublicKey(settings.publicKey || '');
        setSubscription(current);
        setPreferences(preferencesForBusinesses(businesses, settings.subscription?.preferences));
        setDiagnostics(deliveryDiagnostics);
        setPermission(supported ? Notification.permission : 'default');
      } catch (reason) {
        if (!cancelled) setError(reason instanceof Error ? reason.message : 'Notification settings could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [supported, businessKey]);

  const active = Boolean(subscription && permission === 'granted');
  const refreshDiagnostics = async (current = subscription) => {
    if (!current) {
      setDiagnostics(null);
      return;
    }
    setDiagnostics(await communicationsRepository.getPushDiagnostics(current.endpoint));
  };
  const enable = async () => {
    setError('');
    if (!supported) {
      setError(ios && !standalone ? 'On iPhone, add LineWatch to your Home Screen first, then open the installed app and enable notifications here.' : 'This browser does not support PWA push notifications.');
      return;
    }
    if (!configured || !publicKey) {
      setError('Push delivery is not configured on the LineWatch server yet.');
      return;
    }
    if (ios && !standalone) {
      setError('Add LineWatch to your iPhone Home Screen, open the installed app, then tap Enable notifications.');
      return;
    }
    if (Notification.permission === 'denied') {
      setError('Notifications are blocked for LineWatch. Allow them in your phone or browser settings, then try again.');
      return;
    }
    setWorking(true);
    let created: PushSubscription | null = null;
    try {
      const nextPermission = await Notification.requestPermission();
      setPermission(nextPermission);
      if (nextPermission !== 'granted') throw new Error('Notification permission was not granted.');
      const registration = await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();
      const next = current || await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
      if (!current) created = next;
      await communicationsRepository.savePushSubscription(toPushPayload(next), preferences);
      setSubscription(next);
      await refreshDiagnostics(next);
    } catch (reason) {
      if (created) await created.unsubscribe().catch(() => false);
      setError(reason instanceof Error ? reason.message : 'Notifications could not be enabled.');
    } finally { setWorking(false); }
  };

  const disable = async () => {
    if (!subscription) return;
    setWorking(true);
    setError('');
    try {
      await communicationsRepository.deletePushSubscription(subscription.endpoint);
      await subscription.unsubscribe();
      setSubscription(null);
      setDiagnostics(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Notifications could not be disabled.');
    } finally { setWorking(false); }
  };

  const toggle = async (businessId: string, key: keyof PushCategoryPreferences) => {
    if (!subscription || working) return;
    const previous = preferences;
    const line = preferences.lines[businessId] || { ...defaultLinePushPreferences };
    const next = {
      lines: {
        ...preferences.lines,
        [businessId]: { ...line, [key]: !line[key] },
      },
    };
    setPreferences(next);
    setWorking(true);
    setError('');
    try {
      await communicationsRepository.savePushSubscription(toPushPayload(subscription), next);
      await refreshDiagnostics(subscription);
    }
    catch (reason) {
      setPreferences(previous);
      setError(reason instanceof Error ? reason.message : 'The notification preference could not be saved.');
    } finally { setWorking(false); }
  };

  const sendTest = async (business: BusinessLine, category: NotificationCategory) => {
    if (!subscription || testing) return;
    const key = `${business.id}:${category}`;
    setTesting(key);
    setError('');
    setTestStatus('');
    try {
      const result = await communicationsRepository.sendTestNotification(subscription.endpoint, business.id, category);
      setTestStatus(result.status === 'sent'
        ? `${pushCategoryLabel(category)} test delivered for ${business.name}.`
        : `${pushCategoryLabel(category)} test queued for ${business.name}; LineWatch will retry it automatically if needed.`);
      await refreshDiagnostics(subscription);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The test notification could not be sent.');
      await refreshDiagnostics(subscription).catch(() => undefined);
    } finally {
      setTesting('');
    }
  };

  const options: Array<{ key: keyof PushCategoryPreferences; icon: typeof Bell; label: string; shortLabel: string }> = [
    { key: 'newMessages', icon: MessageSquareText, label: 'New messages', shortLabel: 'Messages' },
    { key: 'newCalls', icon: PhoneCall, label: 'New calls', shortLabel: 'Calls' },
    { key: 'needsReview', icon: AlertTriangle, label: 'Needs Review', shortLabel: 'Review' },
  ];

  return <section className="settings-section notification-settings">
    <div className="settings-heading"><h3>Push notifications</h3><span>{active ? 'On for this device' : 'This device'}</span></div>
    <div className={`notification-summary ${active ? 'active' : ''}`}>
      <span className="notification-bell"><Bell size={18} />{active && <i />}</span>
      <div><strong>{active ? 'Notifications are on' : 'Stay on top of every line'}</strong><small>{active ? `${businesses.length} business lines can be tuned independently on this device.` : 'Enable once, then choose exactly what this device receives.'}</small></div>
      <button disabled={loading || working} onClick={() => void (active ? disable() : enable())}>{working ? <Loader2 className="spinning" size={14} /> : null}{active ? 'Turn off' : 'Enable'}</button>
    </div>
    <div className="line-notification-list" aria-busy={working}>
      {businesses.map(business => {
        const line = preferences.lines[business.id] || defaultLinePushPreferences;
        return <article className={`line-notification-card ${!active ? 'disabled' : ''}`} key={business.id}>
          <header><BusinessAvatar business={business} /><div><strong>{business.name}</strong><small>{business.phoneNumber}</small></div></header>
          <div className="line-notification-controls">
            {options.map(option => <div key={option.key}>
              <span><option.icon size={14} /><b>{option.shortLabel}</b></span>
              <span className="line-notification-actions">
                <label className="switch"><input type="checkbox" role="switch" aria-label={`${business.name}: ${option.label}`} checked={line[option.key]} disabled={!active || working || Boolean(testing)} onChange={() => void toggle(business.id, option.key)} /><i /></label>
                <button className="notification-test" disabled={!active || !line[option.key] || working || Boolean(testing)} onClick={() => void sendTest(business, option.key)} aria-label={`Send a ${option.label.toLowerCase()} test for ${business.name}`}>{testing === `${business.id}:${option.key}` ? <Loader2 className="spinning" size={11} /> : <Send size={11} />}<span>Test</span></button>
              </span>
            </div>)}
          </div>
        </article>;
      })}
    </div>
    {active && diagnostics && <PushDeliveryHealth diagnostics={diagnostics} onRefresh={() => void refreshDiagnostics()} refreshing={working || Boolean(testing)} />}
    {testStatus && <div className="notification-test-status" role="status"><CheckCircle2 size={15} /><span>{testStatus}</span></div>}
    {ios && !standalone && !active && <div className="notification-hint"><ShieldCheck size={15} /><span>iPhone requires the installed Home Screen PWA. In Safari, tap Share → Add to Home Screen, then enable notifications inside LineWatch.</span></div>}
    {!configured && !loading && <div className="notification-hint warning"><AlertTriangle size={15} /><span>Server delivery needs its one-time secure key setup before this device can subscribe.</span></div>}
    {error && <div className="compose-error" role="alert"><AlertTriangle size={16} />{error}</div>}
  </section>;
}

function PushDeliveryHealth({ diagnostics, onRefresh, refreshing }: {
  diagnostics: PushDeliveryDiagnostics;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const health = diagnostics.delivery.dead > 0
    ? { label: 'Needs attention', tone: 'attention', icon: AlertTriangle }
    : diagnostics.delivery.retrying > 0 || diagnostics.delivery.queued > 0
      ? { label: 'Delivering', tone: 'working', icon: Clock3 }
      : { label: 'Healthy', tone: 'healthy', icon: CheckCircle2 };
  const HealthIcon = health.icon;
  return <div className={`push-delivery-health ${health.tone}`}>
    <header>
      <span><HealthIcon size={16} /></span>
      <div><strong>Delivery health · {health.label}</strong><small>{diagnostics.delivery.lastSuccessAt ? `Last accepted ${formatNotificationTime(diagnostics.delivery.lastSuccessAt)}` : 'No successful delivery recorded for this device yet.'}</small></div>
      <button onClick={onRefresh} disabled={refreshing} aria-label="Refresh notification delivery health"><RefreshCw className={refreshing ? 'spinning' : ''} size={13} />Refresh</button>
    </header>
    <div className="push-health-metrics" aria-label="Notification delivery totals">
      <span><strong>{diagnostics.delivery.sent}</strong><small>Sent · 24h</small></span>
      <span><strong>{diagnostics.delivery.queued}</strong><small>Queued</small></span>
      <span><strong>{diagnostics.delivery.retrying}</strong><small>Retrying</small></span>
      <span><strong>{diagnostics.delivery.dead}</strong><small>Failed · 24h</small></span>
    </div>
    <p className="push-delivery-policy">Messages remain eligible for {formatPushTtl(diagnostics.policy.ttlSeconds.newMessages)}, calls for {formatPushTtl(diagnostics.policy.ttlSeconds.newCalls)}, and Needs Review for {formatPushTtl(diagnostics.policy.ttlSeconds.needsReview)}. Transient failures retry up to {diagnostics.policy.maxAttempts} times.</p>
    {diagnostics.delivery.recent.length > 0 && <details className="push-delivery-recent">
      <summary>Recent delivery details <span>{diagnostics.delivery.recent.length}</span></summary>
      <div>{diagnostics.delivery.recent.map(item => <article key={item.id}>
        <span className={`push-delivery-state ${item.status}`}>{item.status === 'sent' ? <Check size={12} /> : item.status === 'dead' ? <AlertTriangle size={12} /> : <Clock3 size={12} />}</span>
        <div><strong>{item.businessName} · {pushCategoryLabel(item.category)}{item.isTest ? ' test' : ''}</strong><small>{pushDeliveryStatusLabel(item.status, item.attemptCount)} · {formatNotificationTime(item.deliveredAt || item.updatedAt)}</small>{item.lastError && <p>{item.lastError}</p>}</div>
      </article>)}</div>
    </details>}
  </div>;
}

function pushCategoryLabel(category: NotificationCategory): string {
  if (category === 'needsReview') return 'Needs Review';
  return category === 'newMessages' ? 'Message' : 'Call';
}

function pushDeliveryStatusLabel(status: PushDeliveryDiagnostics['delivery']['recent'][number]['status'], attempts: number): string {
  if (status === 'sent') return 'Delivered';
  if (status === 'dead') return 'Stopped after delivery failure';
  if (status === 'retrying') return `Retry scheduled · attempt ${attempts}`;
  if (status === 'sending') return 'Sending now';
  return 'Queued';
}

function formatPushTtl(seconds: number): string {
  const hours = Math.round(seconds / 3_600);
  if (hours >= 48 && hours % 24 === 0) return `${hours / 24} days`;
  return `${hours} hours`;
}

async function syncAppBadge(count: number): Promise<void> {
  const badgeNavigator = navigator as Navigator & { setAppBadge?: (value?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try {
    if (count > 0 && badgeNavigator.setAppBadge) await badgeNavigator.setAppBadge(count);
    else if (badgeNavigator.clearAppBadge) await badgeNavigator.clearAppBadge();
  } catch {
    // Badging is progressive enhancement; the in-app counter remains authoritative.
  }
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready.catch(() => null);
    registration?.active?.postMessage({ type: 'LINEWATCH_BADGE_SYNC', count });
  }
}

async function closeDeliveredNotifications(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  registration?.active?.postMessage({ type: 'LINEWATCH_NOTIFICATIONS_SEEN' });
}

function toPushPayload(subscription: PushSubscription): PushSubscriptionPayload {
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) throw new Error('The browser returned an incomplete push subscription.');
  return { endpoint: subscription.endpoint, expirationTime: subscription.expirationTime, keys: { p256dh, auth } };
}

function urlBase64ToUint8Array(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const decoded = atob((value + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = Uint8Array.from(decoded, character => character.charCodeAt(0));
  return bytes.buffer as ArrayBuffer;
}

function NotificationCenter({ notifications, loading, dismissingId, clearingId, onOpen, onDismiss, onClear, onClose }: {
  notifications: LineWatchNotification[];
  loading: boolean;
  dismissingId: string | null;
  clearingId: string | null;
  onOpen: (notification: LineWatchNotification) => void;
  onDismiss: (notification: LineWatchNotification) => void;
  onClear: (notification: LineWatchNotification) => void;
  onClose: () => void;
}) {
  const meta = {
    newMessages: { icon: MessageSquareText, label: 'Message' },
    newCalls: { icon: PhoneCall, label: 'Call' },
    needsReview: { icon: AlertTriangle, label: 'Needs review' },
  } as const;
  return <div className="sheet-backdrop notification-backdrop" onClick={onClose}>
    <section className="notification-center" role="dialog" aria-modal="true" aria-label="Recent notifications" onClick={event => event.stopPropagation()}>
      <div className="sheet-handle" />
      <header className="notification-center-heading">
        <div><p className="eyebrow">Across every business line</p><h2>Notifications</h2><p>Opening this list marks new items as viewed.</p></div>
        <button className="icon-button" onClick={onClose} aria-label="Close notifications"><X size={18} /></button>
      </header>
      <div className="notification-feed" aria-live="polite">
        {loading && <div className="notification-empty"><Loader2 className="spinning" size={22} /><strong>Loading notifications</strong></div>}
        {!loading && !notifications.length && <div className="notification-empty"><Bell size={24} /><strong>You’re all caught up</strong><p>New messages, calls, and escalations will appear here.</p></div>}
        {!loading && notifications.map(notification => {
          const item = meta[notification.category];
          const Icon = item.icon;
          const dismissing = dismissingId === notification.communicationId;
          const clearing = clearingId === notification.id;
          return <article className={`notification-row ${notification.category}`} key={notification.id}>
            <button className={`notification-item ${notification.seenAt ? 'seen' : 'unseen'} ${notification.category}`} onClick={() => onOpen(notification)}>
              <span className="notification-line-avatar" style={{ color: notification.businessColor, background: notification.businessSoftColor }}>{notification.businessInitials}</span>
              <span className="notification-item-copy">
                <span className="notification-item-top"><strong>{notification.title}</strong><time>{formatNotificationTime(notification.occurredAt)}</time></span>
                <span className="notification-context"><Icon size={13} />{item.label}<i />{notification.contactName}</span>
                <span className="notification-preview">{notification.body}</span>
              </span>
              <span className="notification-seen-state" aria-label={notification.seenAt ? 'Viewed' : 'New'}>{notification.seenAt ? <CheckCircle2 size={17} /> : <i />}</span>
            </button>
            {notification.category === 'needsReview'
              ? <button className="notification-dismiss" onClick={() => onDismiss(notification)} disabled={dismissing || clearing} aria-label={`Dismiss needs attention for ${notification.contactName}`}>{dismissing ? <Loader2 className="spinning" size={13} /> : <Check size={13} />}<span>{dismissing ? 'Dismissing…' : 'Dismiss'}</span></button>
              : <button className="notification-dismiss notification-clear" onClick={() => onClear(notification)} disabled={clearing || Boolean(dismissingId)} aria-label={`Clear ${notification.title}`}>
                {clearing ? <Loader2 className="spinning" size={13} /> : <X size={13} />}<span>{clearing ? 'Clearing…' : 'Clear'}</span>
              </button>}
          </article>;
        })}
      </div>
    </section>
  </div>;
}

function BusinessPicker({ businesses, businessId, onChoose, onClose }: { businesses: BusinessLine[]; businessId: string; onChoose: (id: string) => void; onClose: () => void }) {
  return <div className="sheet-backdrop" onClick={onClose}><section className="business-sheet" role="dialog" aria-modal="true" aria-label="Choose a business line" onClick={event => event.stopPropagation()}><div className="sheet-handle" /><div className="sheet-title"><div><p className="eyebrow">Calling and messaging identity</p><h2>Choose a business line</h2></div><button className="icon-button" onClick={onClose}><X size={18} /></button></div><button className={`sheet-business ${businessId === 'all' ? 'active' : ''}`} onClick={() => onChoose('all')}><span className="business-avatar all-avatar"><Inbox size={17} /></span><span><strong>All lines</strong><small>Unified activity across every business</small></span>{businessId === 'all' && <Check size={18} />}</button>{businesses.map(business => <button key={business.id} className={`sheet-business ${businessId === business.id ? 'active' : ''}`} onClick={() => onChoose(business.id)}><BusinessAvatar business={business} /><span><strong>{business.name}</strong><small>{business.phoneNumber}</small></span>{businessId === business.id && <Check size={18} />}</button>)}</section></div>;
}

function LoginScreen({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setWorking(true);
    setError('');
    try { await communicationsRepository.login(email, password); onSignedIn(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Sign-in failed.'); }
    finally { setWorking(false); }
  };
  return <div className="auth-screen"><form className="auth-card" onSubmit={submit}><BrandMark /><div><p className="eyebrow">Private workspace</p><h1>Welcome back</h1><p>Sign in to review and send business communications.</p></div><label><span>Email</span><input type="email" autoComplete="username" required value={email} onChange={event => setEmail(event.target.value)} /></label><label><span>Password</span><input type="password" autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} /></label>{error && <div className="compose-error"><AlertTriangle size={16} />{error}</div>}<button disabled={working}>{working ? <Loader2 className="spinning" size={17} /> : <ShieldCheck size={17} />}{working ? 'Signing in…' : 'Sign in securely'}</button></form></div>;
}

function ServiceError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div className="service-error"><AlertTriangle size={28} /><h2>Communications service unavailable</h2><p>{message || 'LineWatch could not load the dashboard.'}</p><button onClick={onRetry}><RefreshCw size={16} /> Try again</button></div>;
}

function SectionHeading({ title, aside }: { title: string; aside: string }) {
  return <div className="section-heading"><h3>{title}</h3><span>{aside}</span></div>;
}

function MobileNavButton({ icon: Icon, label, active, onClick }: { icon: typeof Inbox; label: string; active: boolean; onClick: () => void }) {
  return <button className={active ? 'active' : ''} onClick={onClick}><Icon size={20} /><span>{label}</span></button>;
}

function EmptyState({ onReset, onMessage }: { onReset: () => void; onMessage: () => void }) {
  return <div className="empty-state"><Search size={24} /><h3>No matching communications</h3><p>Try another business, person, number or activity type.</p><div className="empty-actions"><button onClick={onReset}>Clear filters</button><button onClick={onMessage}>New message</button></div></div>;
}

function NoSelection({ onMessage }: { onMessage: () => void }) {
  return <div className="no-selection"><Inbox size={28} /><h3>Select an interaction</h3><p>Call recordings, transcripts and full message threads will appear here.</p><button className="reply-button" onClick={onMessage}><MessageSquareText size={16} /> New message</button></div>;
}

function LoadingScreen() {
  return <div className="loading-screen"><BrandMark /><div className="loading-line"><span /></div><p>Preparing your communications desk…</p></div>;
}

async function prepareMmsAttachment(file: File): Promise<File> {
  if (MMS_DIRECT_TYPES.has(file.type) && file.size <= MMS_SAFE_MAX_BYTES) return file;
  if (!file.type.startsWith('image/')) {
    throw new Error('This attachment must be a supported MMS file no larger than 600 KB.');
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('This photo format could not be prepared for MMS. Try a JPEG, PNG, or WebP image.'));
      element.src = objectUrl;
    });
    const largestSide = Math.max(image.naturalWidth, image.naturalHeight);
    let scale = Math.min(1, 1600 / Math.max(1, largestSide));
    let quality = 0.86;
    for (let attempt = 0; attempt < 9; attempt += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext('2d');
      if (!context) throw new Error('This browser could not prepare the photo for MMS.');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (blob && blob.size <= MMS_SAFE_MAX_BYTES) {
        const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo';
        return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
      }
      scale *= 0.82;
      quality = Math.max(0.52, quality - 0.06);
    }
    throw new Error('The photo could not be reduced to the reliable 600 KB MMS limit.');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function normalizeMmsFile(file: File): File {
  const aliases: Record<string, string> = {
    'image/jpg': 'image/jpeg',
    'audio/x-wav': 'audio/wav',
    'text/x-vcard': 'text/vcard',
    'text/directory': 'text/vcard',
    'application/vcard': 'text/vcard',
    'text/x-vcalendar': 'text/calendar',
    'application/ics': 'text/calendar',
  };
  const extensions: Record<string, string> = {
    '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg', '.png': 'image/png', '.gif': 'image/gif', '.webp': 'image/webp',
    '.mp4': 'video/mp4', '.3gp': 'video/3gpp', '.mov': 'video/quicktime',
    '.mp3': 'audio/mpeg', '.wav': 'audio/wav', '.amr': 'audio/amr', '.ogg': 'audio/ogg',
    '.pdf': 'application/pdf', '.vcf': 'text/vcard', '.ics': 'text/calendar',
  };
  const extension = file.name.match(/\.[^.]+$/)?.[0]?.toLowerCase() || '';
  const contentType = aliases[file.type.toLowerCase()] || file.type || extensions[extension] || '';
  return contentType && contentType !== file.type
    ? new File([file], file.name, { type: contentType, lastModified: file.lastModified })
    : file;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds: number) {
  if (!seconds) return '0:00';
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function formatNotificationTime(value: string) {
  const elapsed = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return 'Now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(value));
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default App;
